import { action, observable, computed } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { mdiCog, mdiPencil, mdiPencilOffOutline, mdiPlusBoxOutline, mdiTrashCanOutline } from '@mdi/js';
import arrowCursorMove from 'cd-eft/assets/images/arrow-cursor-move.svg';
import { SeparatorVM } from '../../common/ContainerWithSideBar/ContainerWithSideBarVM';
import { IProfileSettingsForm, initialFormValidationTerraceDesign } from './TerraceDesignProfileView.config';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { IBlockStationDTO, IDrainStationDTO, ITerraceVertexParamsDTO, ISlopeLimits, SlopeModeChannelEnum, SlopeModeToLimitEnum, TerraceUiState } from 'cd-eft/models/Stores/TerraceDesignStore';
import { DynamicTerraceProfileStore } from 'cd-eft/models/Stores/DynamicProfile/Terrace/DynamicTerraceProfileStore';
import { DynamicTerraceTableStore } from 'cd-eft/models/Stores/DynamicProfile/Terrace/DynamicTerraceTableStore';
import { parseStationString, formatStationString } from 'cd-eft/utils/stationUtilities';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import MapView from '@arcgis/core/views/MapView';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Extent from '@arcgis/core/geometry/Extent';
import { ProfileController, TerraceDesignToolsController, TerraceChannelTemplateController, AlignmentController } from 'cd-eft/services/eftSetApi';
import { saveTerraceReachEdits, clearAllTerraceEdits } from 'cd-eft/components/Design/SurfaceDesigner/SurfaceDesignerVm';
import { AppConstants } from 'cd-eft/appConstants';
import { TerraceReachController, TerraceProfileController } from 'cd-eft/services/eftSetApi';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { trackerSymbol } from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { esriProjection, FeatureLayer } from 'cd-eft/utils/esriTypes';
import { terrraceVertexParamsOnDesign, TerraceTemplateVM } from '../TerraceTemplate/TerraceTemplateVM';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import { DynamicTerraceMapStore } from 'cd-eft/models/Stores/DynamicProfile/Terrace/DynamicTerraceMapStore';
import { TerraceSimulationAndValidationRespDTO, TerraceDesignShapeResponseDTO, TerraceDesignReportStatisticsDTO, TerraceDesignReportSummaryDTO, ErrorResponse, TerraceDesignReportHydrologySectionDTO, TerraceDesignReportInputsDTO, ProjectInfoDTO, TerraceDesignReportStationTableDTO, TerraceDesignReportTemplateDTO } from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { DisplayAlignment } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/loadLayers';
import { appConfig } from 'cd-eft/config/AppConfig';
import { getListOfDesignElements } from 'cd-eft/components/Design/common/ElementContainer/ElementContainerVM';
import sleep from 'cd-eft/utils/sleep';
import { putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import { formatRoundSTrim } from 'cd-eft/utils/mathUtilities';
import { createAllTerraceScreenshot } from 'cd-eft/components/common/Screenshot/terraceScreenshot';
import { testDecimalString, testStationString } from 'cd-eft/utils/stringUtilities';
import { shiftElevationCallback, updateStation } from 'cd-eft/models/Stores/DesignStore';
import { generateSurface } from '../TerraceContainer/TerraceContainerVM';

const { designStore, terraceDesignStore, surveyEngineeringStore, projectsLibraryStore } = stores.AppStore;

interface IAdjacentTerracesOrWaterwaysResponseDTO {
  alignedElementParamId: number,
  sourceAlignmentId: number,
  targetAlignmentId: number,
  channelName: string,
  designElementTypeId: number,
  matchedReachStationName: string | undefined,
  sourceAlignmentDistance: number,
  x: number,
  y: number,
  channelElevation: number,
  vertexDescription,
  distance: number,
  theSameAlignmentId: boolean,
  selected: boolean
}

interface ITerraceDesignReportDTO {
  designStatistics?: TerraceDesignReportStatisticsDTO;
  designSummary?: TerraceDesignReportSummaryDTO;
  errorResponse?: ErrorResponse;
  hydrologySection?: TerraceDesignReportHydrologySectionDTO;
  inputs?: TerraceDesignReportInputsDTO;
  projectInfo?: ProjectInfoDTO;
  stationsTable?: Array<TerraceDesignReportStationTableDTO>;
  template?: TerraceDesignReportTemplateDTO;
}

export function getLayerExtent(groupLayer: GroupLayer, layerId: string): Extent | undefined {
  let result: Extent | undefined = undefined;
  const boundaryLayer = groupLayer.findLayerById(layerId);
  if (boundaryLayer) {
    const boundaryGraphics = boundaryLayer as GraphicsLayer;
    if (boundaryGraphics && boundaryGraphics.graphics && (boundaryGraphics.graphics.length > 0)) {
      const boundary = boundaryGraphics.graphics.getItemAt(0).geometry;
      if (boundary && boundary.extent) {
        result = new Extent({
          xmin: boundary.extent.xmin,
          xmax: boundary.extent.xmax,
          ymin: boundary.extent.ymin,
          ymax: boundary.extent.ymax,
          spatialReference: boundary.extent.spatialReference
        });
        // result = boundary.extent.clone();
      }
    }
  }
  return result;
}

/* istanbul ignore next: Unit Tests In Progress */
export function getTerraceMapExtent(terraceLayer: GroupLayer): Extent | undefined {
  let result: Extent | undefined;
  const borderExtent = getLayerExtent(terraceLayer, 'borderPolygon_polygon');
  const subbasinExtent = getLayerExtent(terraceLayer, 'subbasinPolygon_polygon');
  if (borderExtent) {
    if (subbasinExtent) {
      result = borderExtent.union(subbasinExtent);
    } else {
      result = borderExtent;
    }
  } else if (subbasinExtent) {
    result = subbasinExtent;
  }
  return result;
}

export class TerraceDesignProfileViewVM extends SeparatorVM {
  public terraceTemplateVM: TerraceTemplateVM;
  @observable redigitizeReachesDialogIsOpen: boolean = false;
  @observable mapView?: MapView;
  @observable changedInputValue: boolean = false;
  @observable saveButtonPopup: boolean = false;
  @observable terraceDesignReportObj: ITerraceDesignReportDTO = {};
  @observable headerTitle = "";
  protected profileStoreRef?: DynamicTerraceProfileStore;
  protected tableStoreRef?: DynamicTerraceTableStore;
  protected profileStoreBaseRef?: DynamicProfileStoreBase;
  @observable matchedVertices: IAdjacentTerracesOrWaterwaysResponseDTO[] = [];
  @observable showMatchVertexModal: boolean = false;
  @observable selectedVertexIndex: number | undefined;
  @observable targetStationIndex: number = 0; // The index of the station that will be modified.
  @observable matchTargetStationName: string = ''; // Used for the life of the match vertex modal. This value should not change when user applies a selection.
  @observable matchSearchDistanceState = {
    distance: 20 as number | undefined,
    valid: true as boolean
  };
  @observable shiftElevation = 0.0.toFixed(1);
  @observable shiftElevationDialogIsOpen: boolean = false;
  @observable profileParametersChanged: boolean = false;
  fixedWidthOrDepth: 'depth' | 'width' | 'none' = 'none';
  @observable fixedValue = 0.0.toFixed(1);
  private cancelFlag = false;
  @observable existingDrainStations: {
    stationIndex: number,
    stationName: string,
    drainStationDto: IDrainStationDTO
  }[] = [];
  @observable totalDrainFraction: number = 0;
  history: any;
  @observable onClickProfileInformation = false;

  constructor(props) {
    super(props);
    this.terraceTemplateVM = new TerraceTemplateVM(this.history);
    this.mapView = props.mapView;
    this.history = history;
    if (props.profileStoreRef) {
      this.profileStoreRef = props.profileStoreRef;
      this.profileStoreRef!.deleteAction = (index: number) => {
        this.terraceDesignStationToDelete.reachId = 'R-' + index;
        this.toggleDeleteStationDialog();
      }
    }
    if (props.tableStoreRef) {
      this.tableStoreRef = props.tableStoreRef;
      this.tableStoreRef!.setStore = surveyEngineeringStore;
      this.tableStoreRef!.terraceStore = terraceDesignStore;
      this.tableStoreRef!.CurrentObject = terraceDesignStore.terraceDesignObject;
    }
    // this.mapView = DesignContainerVM.mapView;
    this.getErrors();
  }

  // free: whenever any input on Terrace Input Page changes, the state is set free
  // otherState: TerraceInputSave, TerraceInputProceed, ChannelDesignSave ...
  @computed get uiState(): "free" | "ChannelDesignSave" | "ChannelDesignCalculate" | "ChannelDesignSurface" | 'otherState' {

    let uiState: "free" | "ChannelDesignSave" | "ChannelDesignCalculate" | "ChannelDesignSurface" | 'otherState';

    const globalUIState = terraceDesignStore.uiState;

    if (globalUIState === undefined || globalUIState === 'TerraceTemplateProceed' || this.profileParametersChanged || designStore.terraceGroupsLength !== 0) {

      uiState = 'free';

    } else if (globalUIState === 'ChannelDesignSave' || globalUIState === 'ChannelDesignCalculate' || globalUIState === 'ChannelDesignSurface') {

      uiState = globalUIState as 'ChannelDesignSave' | 'ChannelDesignCalculate' | 'ChannelDesignSurface';

    } else {

      uiState = 'otherState'

    }
    return uiState
  }

  @computed get disableActionButton() {
    const stationCount = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;
    return !terraceDesignStore.uiState || (TerraceUiState[terraceDesignStore.uiState!].number < 12 && stationCount !== 0) || (TerraceUiState[terraceDesignStore.uiState!].number < 14 && stationCount === 0);
  }

  @computed get profileButtons() {
    const stationCount = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;

    return [
      {
        icon: mdiCog,
        title: 'Settings',
        disabled: this.disableActionButton,
        onClick: this.openSettings,
        className: 'mr-auto'
      },
      {
        icon: mdiPencil,
        title: 'Digitize Channels',
        disabled: this.disableActionButton,
        onClick: this.sketchReaches
      },
      {
        icon: mdiPlusBoxOutline,
        title: 'Insert Station',
        disabled: this.disableActionButton,
        onClick: this.insertTerraceVertex
      },
      {
        img: arrowCursorMove,
        title: 'Move Station',
        alt: '',
        disabled: this.disableActionButton,
        onClick: this.moveVertex,
        imgClassName: 'arrowCursorMove'
      },
      {
        icon: mdiTrashCanOutline,
        title: 'Delete Station',
        disabled: (stationCount <= 3 && stationCount !== 0) || this.disableActionButton,
        onClick: this.deleteVertex
      },
      {
        icon: mdiPlusBoxOutline,
        title: 'Add Drain',
        disabled: this.disableActionButton || (terraceDesignStore.terraceType === 20 || terraceDesignStore.terraceType === 21),
        onClick: () => this.openModalFromProfile('drain')
      },
      {
        icon: mdiPlusBoxOutline,
        title: 'Add Block',
        disabled: this.disableActionButton || (terraceDesignStore.terraceType === 20),
        onClick: () => this.openModalFromProfile('block')
      },
      {
        icon: mdiPencilOffOutline,
        title: '',
        disabled: this.disableActionButton,
        alt: 'Stop Editing',
        onClick: this.stopEditing,
      },
    ];
  }
  get disableSaveButton() {
    return (designStore.terraceGroupsLength === 0 && !this.profileParametersChanged) || this.uiState === 'ChannelDesignSave' || this.uiState === 'otherState' || this.disableActionButton;
  }
  get disableSimulateButton() {
    return true || this.disableActionButton;
  }
  get disableSaveAndCalculateButton() {
    return designStore.terraceGroupsLength === 0 && (terraceDesignStore.terraceDesignObject?.terraceDesignStations?.length === 0 || terraceDesignStore.displayResultBanner || this.formValidationReachTable.invalidFields.length > 0 || this.disableActionButton);
  }
  get disableGenerateSurfaceButton() {
    return !terraceDesignStore.generateTerraceSurface || !terraceDesignStore.displayResultBanner || this.disableActionButton;
  }

  get reachTableButtons() {
    return [
      { title: 'Close', disabled: false, onClick: this.handleCancelEdits, className: 'usa-button--outline' },
      { title: 'Save', disabled: this.disableSaveButton, onClick: this.onClickSave, className: 'usa-button--outline' },
      { title: 'Simulate', disabled: this.disableSimulateButton, onClick: () => { }, className: 'usa-button--outline' },
      { title: 'Save & Calculate', disabled: this.disableSaveAndCalculateButton, onClick: this.onClickSaveCalculate },
      { title: 'Generate Surface', disabled: this.disableGenerateSurfaceButton, onClick: this.generateSurface, className: "mr-0" },
    ];
  }
  @observable activeTab: string = 'combined-view';
  @observable showDrainModal = false;
  @observable onClickInformation = false;
  @observable selectedReachId = "";
  @observable errorCode = "";
  @observable errorMessage = "";
  @action.bound onClickInformationToggle = (reachId) => {
    const reach = terraceDesignStore.terraceDesignObject.terraceDesignStations.find(r => r.reachId === reachId);
    this.selectedReachId = reachId
    if (reach?.errorResponse) {
      this.errorCode = reach.errorResponse.errorCode || "Test";
      this.errorMessage = reach.errorResponse.errorMessage || "Test";
    }
    this.onClickInformation = !this.onClickInformation
  }

  @action.bound onClickProfileInformationToggle = (title) => {
    this.headerTitle = title
    this.onClickProfileInformation = !this.onClickProfileInformation
  }

  @observable drainModalState: IDrainStationDTO = {
    drainName: '',
    drainFraction: 0,
    drainOffset: 0,
    ugoAndInlet: null,
    location: {
      id: 0,
      pointName: null,
      description: null,
      tinnable: true,
      visible: true,
      wkid: 0,
      x: 0,
      y: 0,
      z: 0
    }
  };
  @observable showBlockModal = false;
  @observable blockModalState: IBlockStationDTO = {
    sideSlopeMode: 1,
    sideSlope: 0,
    limits: 0,
    topWidth: 0,
    isSplitSubbasin: false,
    blockHeight: 'FULL_HEIGHT',
    location: {
      id: 0,
      pointName: null,
      description: null,
      tinnable: true,
      visible: true,
      wkid: 0,
      x: 0,
      y: 0,
      z: 0,
    }
  };
  @observable showEditReachTemplateModal = false;

  private startTerraceEditing: boolean = false;
  private watchTerraceEditingHandle1;
  private watchTerraceEditingHandle2;
  private watchTerraceEditingHandle3;

  @action.bound insertTerraceVertex(e: any) {
    e.preventDefault();
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.startTerraceEditingMode();
    this.profileStoreRef?.SketchPoint(this.props.addTerraceEdits);
  }

  @action.bound deleteVertex(e: any) {
    e.preventDefault();
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.startTerraceEditingMode();
    this.profileStoreRef?.deletePoint()
  }

  @action.bound stopEditing(e: any) {
    e.preventDefault();
    this.stopTerraceEditingMode();
  }

  private stopTerraceEditingMode() {
    this.profileStoreRef?.stopEditing();
    this.watchTerraceEditingHandle1?.remove();
    this.watchTerraceEditingHandle2?.remove();
    this.watchTerraceEditingHandle3?.remove();
    this.startTerraceEditing = false;
  }
  private handleTerraceEditingClick() {
    if (this.startTerraceEditing) {
      this.stopTerraceEditingMode();
    }
  }
  private startTerraceEditingMode() {
    this.startTerraceEditing = true;
    const profileBtns = document.getElementById('terrace-design-profile-btns');
    const header = document.getElementsByClassName('profile-reaches-info');
    const btns = document.getElementById('terrace-design-btns');
    this.watchTerraceEditingHandle1 = profileBtns?.addEventListener('click', this.handleTerraceEditingClick.bind(this));
    this.watchTerraceEditingHandle2 = header[0]?.addEventListener('click', this.handleTerraceEditingClick.bind(this));
    this.watchTerraceEditingHandle3 = btns?.addEventListener('click', this.handleTerraceEditingClick.bind(this));
  }
  @action.bound async handleDrainModalChange(changes: Partial<IDrainStationDTO>) {
    terraceDesignStore.terraceInputsChanged = true
    // this.uiState = 'free';
    this.drainModalState = {
      ...this.drainModalState,
      ...changes
    };
    this.calculateTotalDrainFraction();
  }

  @action.bound async handleExistingDrainChange(fieldName: string, value, index: number) {
    terraceDesignStore.terraceInputsChanged = true
    this.existingDrainStations[index].drainStationDto[fieldName] = value;

    this.calculateTotalDrainFraction()
  }

  @action.bound async handleBlockModalChange(changes: Partial<IBlockStationDTO>) {
    terraceDesignStore.terraceInputsChanged = true;
    // this.uiState = 'free';
    this.blockModalState = {
      ...this.blockModalState,
      ...changes
    };
  }

  private calculateTotalDrainFraction() {
    const currentFraction = this.drainModalState.drainFraction;
    let total = 0;
    this.existingDrainStations.forEach((station) => {
      if (station.drainStationDto.drainFraction && typeof station.drainStationDto.drainFraction === "number" && Number(station.drainStationDto.drainFraction) > 0) {
        total += station.drainStationDto.drainFraction;
      }
    });
    if (currentFraction && typeof currentFraction === "number" && Number(currentFraction) > -1) {
      total += currentFraction;
    }
    this.totalDrainFraction = total;
  }

  @observable selectedStation: ITerraceVertexParamsDTO[] = [];
  @action.bound setTab = (selectedTab) => {
    if (this.activeTab !== selectedTab) this.activeTab = selectedTab;
  };

  @observable formValidationReachTable = { ...initialFormValidationTerraceDesign };

  @action.bound editReachTemplateModal = (e: any, item: ITerraceVertexParamsDTO) => {
    this.selectedStation = [item];
    // Add the reachId to checkedRowStations if not already included
    if (!terraceDesignStore.checkedRowStations.includes(item.reachId)) {
      terraceDesignStore.checkedRowStations.push(item.reachId);
    }
    this.terraceTemplateVM.applyValuesToTemplate({ ...item, ...item.templateParams });
    this.showEditReachTemplateModal = true;
  }

  combinedAndStationViewActions = {
    'Insert Station': { action: this.insertStationTerraceTableView },
    'Delete Station': {
      action: this.deleteStationTableView,
      disabled: terraceDesignStore.terraceDesignObject.terraceDesignStations.length <= 3
    },
    'Add Block': { action: this.addBlockModal, disabled: (terraceDesignStore.terraceType === 20) },
    'Add Drain': { action: this.addDrainModal, disabled: (terraceDesignStore.terraceType === 21) },
  };

  templateViewActions = {
    'Edit Template': { action: this.editReachTemplateModal },
    'Reset Reach': { action: this.resetReach },
  };
  get profileViewActions() {
    return {
      'Shift Terrace Elevation': { action: this.setShiftElevation, disabled: this.disableActionButton },
      'Match Vertex': { action: () => this.openModalFromProfile('match'), disabled: this.disableActionButton },
    };
  }

  @observable userLeavingWarningIsShown: boolean = false;
  @observable notification: any = {
    redigitizeReaches: '',
    userLeavingWarning: "",
  }

  @action.bound async getErrors() {
    this.notification.redigitizeReaches = await MessageCenter.addErrorLabelFromCode('Profile_02');
    this.notification.userLeavingWarning = await MessageCenter.addErrorLabelFromCode('Common_15');
  }
  @action.bound async closeAction() {
    this.history.push(`/design/${stores.AppStore.designStore.projectsStore.currentProjectId}/terrace`);
  }
  @action.bound async handleCancelEdits() {
    if (designStore.terraceGroupsLength > 0) {
      MessageCenter.addWarning(terraceDesignStore.notifications.unsavedEditsClosePage, [
        {
          callback: () => {
            MessageCenter.clearMessages();
          },
          text: 'Cancel',
        },
        {
          callback: async () => {
            MessageCenter.clearMessages();
            stores.AppStore.terraceDesignStore.reset()
            clearAllTerraceEdits();
            // Use a delay to allow state changes before redirecting
            setTimeout(() => {
              this.closeAction();
            }, 0);
          },
          text: 'OK',
        },
      ], 'Common_13');
    } else {
      MessageCenter.clearMessages();
      stores.AppStore.terraceDesignStore.reset()
      this.closeAction();
    }
  }

  removeReachLabelLayers() {
    if (this.mapView && this.mapView.map) {
      const layers = this.mapView.map.layers.toArray();

      layers.forEach(layer => {
        if (layer.type === 'group' && layer.id.startsWith('alignment')) {
          const groupLayer = layer as GroupLayer;

          const reachStationLayer = groupLayer.findLayerById('reach_stations_layer') as FeatureLayer;
          if (reachStationLayer) {
            const removed = groupLayer.remove(reachStationLayer);
            if (removed) removed.destroy();
          }

          const reachStationLabelLayer = groupLayer.findLayerById('reach_station_labels_layer') as FeatureLayer;
          if (reachStationLabelLayer) {
            const removed = groupLayer.remove(reachStationLabelLayer);
            if (removed) removed.destroy();
          }
        }
      });
    }
  }

  @action.bound async generateInitialProfiles() {
    // MARKER COMMENT
    if (terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId > 0 && terraceDesignStore.summaryInfo.channelLength > 0) {
      await this.init();
    }
    await this.initialLoadTerraceDesignTable();

    await this.profileStoreRef?.generateTerraceProfile(Number(this.profileSettingsForm.input.sampleInterval), Number(this.profileSettingsForm.input.verticalInterval), terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    let gridValue: number = 0;
    if (this.profileSettingsForm.checked.showFineGrid) {
      gridValue = Number(this.profileSettingsForm.input.fineGrid);
      this.profileStoreRef!.updateGridValues(gridValue);
    } else {
      this.profileStoreRef!.updateGridValues(0);
    }
    await this.displayAlignment();
    await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
  }

  @action.bound sketchReaches(e: any) {
    e.preventDefault();
    if (this.profileStoreRef?.checkSketch()) {
      this.toggleRedigitizeReachesDialog();
    } else {
      this.digitizeReaches();
    }
  }
  @action.bound moveVertex(e: any) {
    e.preventDefault();
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.startTerraceEditingMode();
    this.profileStoreRef?.reshapePolyline(this.props.addTerraceEdits);
  }
  @action.bound toggleRedigitizeReachesDialog() {
    this.redigitizeReachesDialogIsOpen = !this.redigitizeReachesDialogIsOpen;
  }
  @action.bound async digitizeReaches() {
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    if (this.redigitizeReachesDialogIsOpen) this.toggleRedigitizeReachesDialog();
    try {
      await TerraceReachController.deleteAllTerraceReachWithAlignedElementDesignParametersIdUsingPOST(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
      await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.TerraceTemplateProceed.string });
      terraceDesignStore.constructedProfileStations = [];
      terraceDesignStore.waterLevelData = [];
      terraceDesignStore.settledProfileStations = [];
      terraceDesignStore.displayResultBanner = false;
      this.closeMatchVertexModal();
      clearAllTerraceEdits();
      this.profileStoreRef?.resetSecondaryLayers();
      terraceDesignStore.hasCalculatedData = true;
      this.profileStoreRef?.sketchPolyline();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound handleReachTemplateInputChange(event: any, index?: number) {
    this.terraceTemplateVM.handleInputChange(event, index);
  }

  @action.bound handleInputChange(event: any, index: number, fieldName: string) {
    const { value } = event.target;
    const terraceDesignStations = terraceDesignStore.terraceDesignObject.terraceDesignStations;

    if (fieldName.includes('.')) {
      const [parentKey, childKey] = fieldName.split('.');
      if (!terraceDesignStations[index][parentKey]) {
        terraceDesignStations[index][parentKey] = {};
      }
      terraceDesignStations[index][parentKey][childKey] = value;
    } else {
      terraceDesignStations[index][fieldName] = value;
    }
    terraceDesignStations[index].status = 'IN PROGRESS';
    this.changedInputValue = true;
    this.validateFieldReachTable(fieldName, value, index);
  }

  @action.bound async validateFieldReachTable(fieldName: string, value: string, index: number) {
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidationReachTable[keyValid];
    const fieldValidationErrors = { ...this.formValidationReachTable.formErrors };
    let errorCode: string = '';

    if (fieldName === 'channelElev') {
      if (value.trim() === '') {
        fieldValid = true;
        fieldValidationErrors[fieldName] = '';
      } else {
        // Allow zero and numbers ending with decimal point
        const isValidNumber = testDecimalString(value.trim());//
        const numericValue = parseFloat(value);
        if (!isValidNumber || isNaN(numericValue) || numericValue < 0) {
          errorCode = 'Common_10';
          fieldValid = false;
        } else {
          fieldValid = true;
          fieldValidationErrors[fieldName] = '';
        }
      }
    } else if (
      fieldName === 'endStation' &&
      (!testStationString(value))
    ) {
      errorCode = fieldName === 'endStation' ? 'WDT_101' : 'Common_10';
      fieldValid = false;
    } else if (fieldName === 'frontHeight' || fieldName === 'bottomWidth') {
      if (value.trim() === '') {
        fieldValid = true;
        fieldValidationErrors[fieldName] = '';
      } else {
        // Allow zero and numbers ending with decimal point
        const isValidNumber = testDecimalString(value.trim());
        const numericValue = parseFloat(value);
        if (!isValidNumber || isNaN(numericValue) || numericValue < 0) {
          errorCode = 'Common_10';
          fieldValid = false;
        } else {
          fieldValid = true;
          fieldValidationErrors[fieldName] = '';
        }
      }
    }

    else if (fieldName.startsWith('templateParams.')) {
      if (value.trim() === '') {
        fieldValid = true;
        fieldValidationErrors[fieldName] = '';
      } else {
        // Allow decimal numbers (e.g., 1.8)
        const isValidDecimal = testDecimalString(value.trim());
        const numericValue = parseFloat(value);

        if (!isValidDecimal || isNaN(numericValue) || numericValue <= 0) {
          errorCode = 'Common_10';
          fieldValid = false;
        } else {
          fieldValid = true;
          fieldValidationErrors[fieldName] = '';
        }
      }
    } else {
      fieldValid = true;
    }

    this.formValidationReachTable.formErrors = fieldValidationErrors;
    await this.getErrorMessage(errorCode, this.formValidationReachTable.formErrors, fieldName);
    this.formValidationReachTable[keyValid] = fieldValid;

    if (!fieldValid) {
      if (!this.formValidationReachTable.invalidFields.some(field => field.fieldName === fieldName && field.index === index)) {
        this.formValidationReachTable.invalidFields.push({ fieldName, index });
      }
    } else {
      this.formValidationReachTable.invalidFields = this.formValidationReachTable.invalidFields.filter(
        field => !(field.fieldName === fieldName && field.index === index)
      );
    }

    this.validateFormReachTable();
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
      return;
    }
    let formattedField: string;
    if (fieldName.startsWith('templateParams.')) {
      // Handle nested fields (e.g., templateParams.cutSlope -> Cut Slope)
      const [, paramName] = fieldName.split('.');
      formattedField = paramName
        .split(/(?=[A-Z])/)  // Split on capital letters
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    } else {
      // Handle simple fields
      switch (fieldName) {
        case 'endStation':
          formattedField = 'Station';
          break;
        case 'channelElev':
          formattedField = 'Channel Z';
          break;
        case 'frontHeight':
          formattedField = 'Front Height';
          break;
        case 'bottomWidth':
          formattedField = 'Bottom Width';
          break;
        default:
          formattedField = fieldName;
      }
    }
    await MessageCenter.addNotificationFromCode(code, undefined, formattedField);
  }

  @action.bound validateFormReachTable() {
    let valid = true;
    for (const key in this.formValidationReachTable.formErrors) {
      if (!this.formValidationReachTable[key + 'Valid']) {
        valid = false;
      }
      if (!valid) {
        break;
      }
    }
    this.formValidationReachTable.formValid = valid;
  }

  @action.bound async handleTableInputChange(event: any, index: number, fieldName: string) {
    if (!this.changedInputValue) {
      return;
    }
    // this.uiState = 'free';
    this.changedInputValue = false;
    if (fieldName === 'channelElev' || fieldName === 'endStation' || fieldName === 'frontHeight' || fieldName === 'bottomWidth') {
      if (!this.formValidationReachTable[`${fieldName}Valid`]) return;
    } else if (fieldName.startsWith('templateParams.')) {
      const [parentKey, childKey] = fieldName.split('.');
      if (!this.formValidationReachTable[`${parentKey}.${childKey}Valid`]) return;
    }
    const { value } = event.target;

    const terraceDesignStations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    if (this.reachTableButtons[1].disabled || this.reachTableButtons[3].disabled) {
      terraceDesignStore.terraceInputsChanged = true
    }
    if (fieldName.startsWith('templateParams.')) {
      const [parentKey, childKey] = fieldName.split('.');
      if (!terraceDesignStations[index][parentKey]) {
        terraceDesignStations[index][parentKey] = {};
      }
      // Validate templateParams values
      const fieldDisplayNames: Record<string, string> = {
        'cutSlope': 'Cut Slope',
        'bottomWidth': 'Bottom Width'
        // Add more field mappings as needed
      };

      const numericValue = parseFloat(value);
      if (!value || value.trim() === '' || isNaN(numericValue) || numericValue <= 0) {
        // Get display name from mapping, fallback to childKey if not found
        const field = fieldDisplayNames[childKey] || childKey;
        await MessageCenter.addNotificationFromCode('Common_08', undefined, field);
        this.formValidationReachTable[`${parentKey}.${childKey}Valid`] = false;
        if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === fieldName && f.index === index)) {
          this.formValidationReachTable.invalidFields.push({ fieldName, index });
        }
        return;
      }

      terraceDesignStations[index][parentKey][childKey] = numericValue;
      this.formValidationReachTable[`${parentKey}.${childKey}Valid`] = true;
      this.formValidationReachTable.invalidFields = this.formValidationReachTable.invalidFields.filter(
        field => !(field.fieldName === fieldName && field.index === index)
      );
      await this.editStationTerraceTableView(event, terraceDesignStations[index], fieldName);
      return;
    }

    // Existing validation for channelElev and endStation
    if ((fieldName === 'channelElev' || fieldName === 'endStation' || fieldName === 'frontHeight' || fieldName === 'bottomWidth') && (!value || value.trim() === '')) {
      const displayNames = {
        'channelElev': 'Channel Z',
        'endStation': 'Station',
        'frontHeight': 'Front Height',
        'bottomWidth': 'Bottom Width'
      };
      const field = displayNames[fieldName] ?? fieldName;
      await MessageCenter.addNotificationFromCode('Common_08', undefined, field);
      this.formValidationReachTable[fieldName + 'Valid'] = false;
      if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === fieldName && f.index === index)) {
        this.formValidationReachTable.invalidFields.push({ fieldName, index });
      }
      return;
    }
    const allFieldsValid = this.formValidationReachTable.channelElevValid && this.formValidationReachTable.endStationValid;
    if (!allFieldsValid) {
      return;
    }

    await this.editStationTerraceTableView(event, terraceDesignStations[index], fieldName);
  }

  @action.bound async editStationTerraceTableView(e: any, item: any, name: string) {
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const index = stations.findIndex((station) => station.reachId === item.reachId);
    if (index === -1) return;

    updateStation(item, stations, index);

    // Handle each field type separately
    const editedStationObject = new Map();

    if (name === 'channelElev') {
      const channelElev = stations[index].channelElev;
      if (channelElev <= 0) {
        await MessageCenter.addNotificationFromCode('Common_10', undefined, "Channel Z");
        // Add field to invalid fields tracking
        this.formValidationReachTable[`${name}Valid`] = false;
        if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === name && f.index === index)) {
          this.formValidationReachTable.invalidFields.push({ fieldName: name, index });
        }
        return;
      }

      const editedValue = parseFloat(channelElev as any).toFixed(1);
      stations[index].channelElev = parseFloat(editedValue);

      stations[index].cutFill = parseFloat((channelElev - stations[index].groundElev).toFixed(1));
      this.recalculateSlopeAndLength(stations, index);

      if (index + 1 < stations.length) {
        this.recalculateSlopeAndLength(stations, index + 1);
      }

      editedStationObject.set(stations[index].reachId, stations[index]);
      if (index + 1 < stations.length) {
        editedStationObject.set(stations[index + 1].reachId, stations[index + 1]);
      }
      await this.props.addTerraceEdits(editedStationObject, false, true);
      await this.updateProfileOnReachPointEdit(stations[index], index);
    } else if (name === 'endStation') {
      const currentStation = stations[index].endStation;
      let editedValue;

      // Handle both input formats
      if (currentStation.indexOf("+") !== -1) {
        editedValue = formatStationString(parseStationString(currentStation).toString());
      } else {
        editedValue = formatStationString(parseFloat(currentStation).toFixed(1));
      }

      // Parse for validation
      const endStationValue = parseStationString(editedValue);
      if (item.reachId === 'R-0') { // R-0 can be 0
        if (endStationValue < 0) {
          await MessageCenter.addNotificationFromCode('WDT_101', undefined, "Station");
          this.formValidationReachTable[`${name}Valid`] = false;
          if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === name && f.index === index)) {
            this.formValidationReachTable.invalidFields.push({ fieldName: name, index });
          }
          return;
        }
      } else if (endStationValue <= 0) {
        await MessageCenter.addNotificationFromCode('Common_10', undefined, "Station");
        // Add field to invalid fields tracking
        this.formValidationReachTable[`${name}Valid`] = false;
        if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === name && f.index === index)) {
          this.formValidationReachTable.invalidFields.push({ fieldName: name, index });
        }
        return;
      }

      // Update current station
      stations[index].endStation = editedValue;
      // Update next station's start value
      if (index + 1 < stations.length) {
        stations[index + 1].startStation = stations[index].endStation;
      }

      // Ground elevation recalculation
      const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST({
        alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
        reachStationDistance: endStationValue - parseStationString(stations[1].startStation),
      });

      if (response.data?.groundElevation !== undefined) {
        stations[index].groundElev = parseFloat(response.data.groundElevation.toFixed(1));
        stations[index].cutFill = parseFloat((stations[index].channelElev - stations[index].groundElev).toFixed(1));
      }

      this.recalculateSlopeAndLength(stations, index);
      if (index + 1 < stations.length) {
        this.recalculateSlopeAndLength(stations, index + 1);
      }

      editedStationObject.set(stations[index].reachId, stations[index]);
      if (index + 1 < stations.length) {
        editedStationObject.set(stations[index + 1].reachId, stations[index + 1]);
      }
      await this.props.addTerraceEdits(editedStationObject, false, true);
      this.updateProfileOnReachPointEdit(stations[index], index);
    } else if (name === 'frontHeight') {
      const frontHeightNum = parseFloat(stations[index].frontHeight.toString());
      if (isNaN(frontHeightNum) || frontHeightNum <= 0) {
        await MessageCenter.addNotificationFromCode('Common_10', undefined, "Front Height");
        return;
      }
      // Update both frontHeight properties
      stations[index].frontHeight = frontHeightNum;
      stations[index].frontHeightEdited = true;
      stations[index].templateParams.frontHeight = frontHeightNum;
      editedStationObject.set(stations[index].reachId, stations[index]);
      await this.props.addTerraceEdits(editedStationObject, false, true);
    }
    else if (name === 'bottomWidth') {
      const bottomWidthNum = parseFloat(stations[index].bottomWidth.toString());
      if (isNaN(bottomWidthNum) || bottomWidthNum <= 0) {
        await MessageCenter.addNotificationFromCode('Common_10', undefined, "Bottom Width");
        return;
      }
      // Update both frontHeight properties
      stations[index].bottomWidth = bottomWidthNum;
      stations[index].bottomWidthEdited = true;
      stations[index].templateParams.bottomWidth = bottomWidthNum;
      editedStationObject.set(stations[index].reachId, stations[index]);
      await this.props.addTerraceEdits(editedStationObject, false, true);
    }
    else if (name.startsWith('templateParams.')) {
      const [parentKey, childKey] = name.split('.');

      // Format number to 2 decimal places and remove trailing zeros
      const formattedValue = formatRoundSTrim(parseFloat(stations[index][parentKey][childKey]), 2) ?? 'NaN';

      const paramValue = parseFloat(formattedValue);
      if (isNaN(paramValue) || paramValue <= 0) {
        await MessageCenter.addNotificationFromCode('Common_10', undefined, childKey === 'cutSlope' ? "Cut Slope" : childKey);
        // Add field to invalid fields tracking
        this.formValidationReachTable[`${parentKey}.${childKey}Valid`] = false;
        if (!this.formValidationReachTable.invalidFields.some(f => f.fieldName === name && f.index === index)) {
          this.formValidationReachTable.invalidFields.push({ fieldName: name, index });
        }
        return;
      }

      stations[index][parentKey][childKey] = paramValue;

      // Sync R-0 and R-1 template params
      // If R-1 was edited, update R-0 too
      if (item.reachId === 'R-1') {
        const r0Index = stations.findIndex(station => station.reachId === 'R-0');
        if (r0Index !== -1) {
          if (!stations[r0Index][parentKey]) {
            stations[r0Index][parentKey] = {};
          }
          stations[r0Index][parentKey][childKey] = paramValue;
          editedStationObject.set('R-0', stations[r0Index]);
        }
      }
      // If R-0 was edited, update R-1 too
      else if (item.reachId === 'R-0') {
        const r1Index = stations.findIndex(station => station.reachId === 'R-1');
        if (r1Index !== -1) {
          if (!stations[r1Index][parentKey]) {
            stations[r1Index][parentKey] = {};
          }
          stations[r1Index][parentKey][childKey] = paramValue;
          editedStationObject.set('R-1', stations[r1Index]);
        }
      }

      // Only proceed with update if no invalid fields exist
      if (this.formValidationReachTable.invalidFields.length === 0) {
        editedStationObject.set(stations[index].reachId, stations[index]);
        await this.props.addTerraceEdits(editedStationObject, false, true);
      }
    }
    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
  }

  async updateProfileOnReachPointEdit({ endStation, channelElev }, index) {
    const editedStation = [endStation, channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)];
    const info = { index: index, point: editedStation }
    await this.profileStoreRef?.editTerrace(info);
  }

  // Helper method to recalculate slope and length
  recalculateSlopeAndLength(stations: any[], currentIndex: number) {
    const current = stations[currentIndex];
    const prev = stations[currentIndex - 1];
    if (current && prev && prev.reachId !== 'START') {
      const deltaY = prev.channelElev - current.channelElev;
      const prevEndStation = parseStationString(prev.endStation ?? '');
      const currentEndStation = parseStationString(current.endStation ?? '');
      const deltaX = prevEndStation - currentEndStation;
      current.grade = deltaX !== 0 ? (deltaY / deltaX) : 0;
      current.stationDist = Math.abs(deltaX);
      current.status = 'IN PROGRESS';
    }
  }


  @action.bound async insertStationTerraceTableView(e: any, item: any) {
    const stations1 = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const stations = JSON.parse(JSON.stringify(stations1));
    const originalStations = JSON.parse(JSON.stringify(stations));

    const index = stations.findIndex(station => station.reachId === item.reachId);
    // Create a deep copy of the item
    const newItem = JSON.parse(JSON.stringify(item));
    newItem.endStation = stations[index].endStation;

    if (index > 0) {
      // Remove '+' sign from endStation and startStation
      const startStation = parseFloat((stations[index].startStation?.replace('+', '') ?? '0'));
      const endStation = parseFloat((stations[index].endStation?.replace('+', '') ?? '0'));
      stations[index].endStation = formatStationString(((startStation + endStation) / 2.0).toFixed(1).toString());
      newItem.startStation = stations[index].endStation;


      // Calculate the average point of channelZ and the channelZ of the station above it
      const currentChannelZ = parseFloat(stations[index].channelElev.toString());
      const prevChannelZ = parseFloat(stations[index - 1].channelElev.toString());
      stations[index].channelElev = parseFloat(((currentChannelZ + prevChannelZ) / 2).toFixed(1));
      // Calculate stationLength and grade for the current index
      const prevStation = stations[index - 1];
      if (prevStation && prevStation.reachId !== "START") {
        const deltaCurY = prevStation.channelElev - stations[index].channelElev;
        const deltaCurX = parseStationString(prevStation.endStation?.toString() ?? '0') - parseStationString(stations[index].endStation?.toString() ?? '0');
        stations[index].grade = deltaCurY / deltaCurX;
        stations[index].stationDist = Math.abs(deltaCurX);
      }
    }
    const payload = {
      alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
      reachStationDistance: parseStationString(newItem.endStation?.toString() ?? '0') - parseStationString(stations[1].startStation?.toString() ?? '0')
    };
    const response = await ProfileController.getGroundElevationByReachStationDistanceFromAlignmentUsingPOST(payload);
    if (response.data && response.data.groundElevation !== undefined) {
      newItem.groundElev = parseFloat(response.data.groundElevation.toFixed(1));
      newItem.cutFill = parseFloat((parseFloat(newItem.channelElev) - newItem.groundElev).toFixed(1));
    }

    //Calculate Station Length and grade for the new station item 
    const prevStation = stations[index];
    if (prevStation && prevStation.reachId !== "START") {

      const endStationPrev = prevStation.endStation || '0';
      const endStationNew = newItem.endStation || '0';

      const deltaCurY = prevStation.channelElev - newItem.channelElev;
      const deltaCurX = parseStationString(endStationPrev) - parseStationString(endStationNew);

      newItem.stationDist = stations[index].stationDist;
      if (deltaCurX !== 0) {
        newItem.grade = deltaCurY / deltaCurX;
      }
    }

    prevStation.status = 'IN PROGRESS';
    prevStation.drainStationDto = {
      drainName: null,
      drainFraction: 0,
      drainOffset: 0,
      ugoAndInlet: null,
      location: {
        id: 0,
        pointName: null,
        description: null,
        tinnable: true,
        visible: true,
        wkid: 0,
        x: 0,
        y: 0,
        z: 0
      }
    }

    prevStation.blockStationDto = {
      sideSlopeMode: 0,
      sideSlope: 0,
      limits: 0,
      topWidth: 0,
      isSplitSubbasin: false,
      blockHeight: null,
      location: {
        id: 0,
        pointName: null,
        description: null,
        tinnable: true,
        visible: true,
        wkid: 0,
        x: 0,
        y: 0,
        z: 0
      }
    }

    // Insert the new item at the next index
    stations.splice(index + 1, 0, newItem);

    // Create the array to hold the updated stations
    const stationsInsertedAndUpdated: any = [];

    // Add the current index item to the array
    stationsInsertedAndUpdated.push(stations[index]);

    // Update the reachId of the new item and all subsequent items
    for (let i = index + 1; i < stations.length; i++) {
      if (stations[i].reachId !== "END" && stations[i].reachId !== "START" && stations[i].reachId !== "R-0") {
        const reachIdNumber = parseInt(stations[i].reachId.split('-')[1]);
        stations[i].reachId = `R-${reachIdNumber + 1}`;
        stations[i].status = 'IN PROGRESS'
        stationsInsertedAndUpdated.push(stations[i]);
      }
    }

    // Create a new Map to hold all the updated values
    const allUpdatedValues = new Map();
    // Compare original and updated stations to find the stations that were added or modified
    stationsInsertedAndUpdated.forEach((updatedStation) => {
      const originalStation = originalStations.find((os) => {
        if (updatedStation.originalReachId) {
          return os.reachId === updatedStation.originalReachId;
        }
        return os.endStation === updatedStation.endStation;
      });

      if (!originalStation) {
        // A newly added station
        allUpdatedValues.set("Add", updatedStation);
      } else {
        const isChanged =
          originalStation.endStation !== updatedStation.endStation ||
          originalStation.reachId !== updatedStation.reachId ||
          parseFloat(originalStation.channelElev) !== parseFloat(updatedStation.channelElev);

        if (isChanged) {
          allUpdatedValues.set(originalStation.reachId, updatedStation);
        }
      }
    });

    terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
    await this.props.addTerraceEdits(allUpdatedValues, false, true);
    await this.profileStoreRef?.createReachFromSavedDesign(stations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)

  }

  // delete TerraceDesign station -->
  @observable deleteStationDialogIsOpen: boolean = false;
  @observable terraceDesignStationToDelete: any = {};

  // Delete Station from Table view
  @action.bound async deleteStationTableView(e: any, item: any) {
    e.preventDefault();
    this.terraceDesignStationToDelete.reachId = item.reachId ? item.reachId : 'R-0';
    terraceDesignStore.deleteStationFromTable = true;
    this.toggleDeleteStationDialog();
  }

  @action.bound toggleDeleteStationDialog() {
    this.deleteStationDialogIsOpen = !this.deleteStationDialogIsOpen;
  }

  @action.bound async deleteTerraceDesignStation() {
    const stations1 = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const stations = JSON.parse(JSON.stringify(stations1));

    if (this.terraceDesignStationToDelete.reachId.substring(0, 1) === 'R' && stations.length > 3) {
      const indexToDelete = stations.findIndex(
        (s) => s.reachId === this.terraceDesignStationToDelete.reachId
      );
      const editedTerraceDesignData = new Map();
      const deleteStation = stations[indexToDelete];
      deleteStation.reachId = 'Delete';
      editedTerraceDesignData.set(this.terraceDesignStationToDelete.reachId, deleteStation);

      stations.splice(indexToDelete, 1);

      // Update remaining stations
      for (let i = indexToDelete; i < stations.length; i++) {
        const originalReachId = stations[i].reachId;
        stations[i].reachId = 'R-' + (Number(stations[i].reachId.substring(2)) - 1);

        if (i === indexToDelete) {
          // Update first station after deletion
          stations[i].startStation = stations[i - 1].endStation;
          const endVal = Number((stations[i].endStation ?? '0').replace('+', ''));
          const startVal = Number((stations[i].startStation ?? '0').replace('+', ''));
          stations[i].stationDist = parseFloat((endVal - startVal).toFixed(1));

          stations[i].grade = (stations[i].channelElev - stations[i - 1].channelElev) / stations[i].stationDist;
        }

        stations[i].status = 'IN PROGRESS';
        editedTerraceDesignData.set(originalReachId, stations[i]);
      }

      await this.props.addTerraceEdits(editedTerraceDesignData, false, true);
      terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
      await this.profileStoreRef?.deleteReachVertex({ index: indexToDelete });

      this.terraceDesignStationToDelete.reachId = '';
      this.toggleDeleteStationDialog();

      if (terraceDesignStore.deleteStationFromTable) {
        terraceDesignStore.deleteStationFromTable = false;
        await this.profileStoreBaseRef?.createReachFromSavedDesign(
          stations,
          terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId
        );
      } else {
        this.profileStoreBaseRef?.addCenterReachLabels(stations);
      }
      this.profileStoreRef?.stopEditing();
    } else {
      this.terraceDesignStationToDelete.reachId = '';
      this.toggleDeleteStationDialog();
    }
  }

  @observable drainValidation = {
    hasError: false,
    invalidFields: [] as { fieldName: string; index: number }[]
  };

  @observable blockValidation = {
    formErrors: {
      topWidth: '',
      sideSlope: '',
      limits: ''
    },
    hasError: false
  };

  @action.bound async addDrainModal(e: any, item: ITerraceVertexParamsDTO) {
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const i = stations.indexOf(item)
    if ((i !== 0 && stations[i - 1].drainStation) || (i < stations.length - 1 && stations[i + 1].drainStation)) {
      await MessageCenter.addNotificationFromCode('Terrace_38', undefined);
    } else {
      this.showAddDrainModal(e, item)
    }
  }

  @action.bound showAddDrainModal(e: any, item: ITerraceVertexParamsDTO): Promise<void> {
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    return new Promise<void>(async (resolve) => {
      this.selectedStation = [item];
      this.existingDrainStations = [];
      this.totalDrainFraction = 0;
      terraceDesignStore.terraceDesignObject.terraceDesignStations.forEach((station, index) => {
        if (station !== item && station.drainStation) {
          this.existingDrainStations.push({ stationIndex: index, stationName: station.endStation, drainStationDto: { ...station.drainStationDto } });
        }
      });

      const stationName = item.endStation;

      const coords = (await AlignmentController.getXYByAlignmentAndStationNameUsingPOST({ alignmentDesignElementId: terraceDesignStore.alignmentDesignElementJsonId, stationName })).data;

      this.drainModalState = {
        drainName: item.drainStationDto?.drainName ?? '',
        drainFraction: item.drainStationDto?.drainFraction ?? 0,
        drainOffset: item.drainStationDto?.drainOffset ?? 0,
        ugoAndInlet: item.drainStationDto?.ugoAndInlet ?? null,
        location: item.drainStationDto?.location ?? {
          id: 0,
          pointName: null,
          description: null,
          tinnable: true,
          visible: true,
          wkid: 0,
          x: coords.x,
          y: coords.y,
          z: item.channelElev
        }
      };

      this.calculateTotalDrainFraction();
      this.showDrainModal = true;
      resolve();
    });
  }

  @action.bound addBlockModal(e: any, item: ITerraceVertexParamsDTO): Promise<void> {
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    return new Promise<void>(async (resolve) => {
      this.selectedStation = [item];

      const isNewBlock = !item.blockStationDto;

      const coords = (await AlignmentController.getXYByAlignmentAndStationNameUsingPOST({ alignmentDesignElementId: terraceDesignStore.alignmentDesignElementJsonId, stationName: item.endStation })).data;

      const newBlockModalState = {
        blockHeight: isNewBlock ? 'FULL_HEIGHT' : (item.blockStationDto?.blockHeight || 'FULL_HEIGHT'),
        sideSlopeMode: item.blockStationDto?.sideSlopeMode ?? 1,
        sideSlope: item.blockStationDto?.sideSlope ?? 0,
        limits: item.blockStationDto?.limits ?? 0,
        topWidth: item.blockStationDto?.topWidth ?? 0,
        isSplitSubbasin: item.blockStationDto?.isSplitSubbasin ?? false,
        location: item.blockStationDto?.location ?? {
          id: 0,
          pointName: null,
          description: null,
          tinnable: true,
          visible: true,
          wkid: 0,
          x: coords.x,
          y: coords.y,
          z: item.channelElev
        }
      };

      this.blockModalState = {
        ...newBlockModalState,
        blockHeight: newBlockModalState.blockHeight
      };

      this.showBlockModal = true;
      resolve();
    });
  }

  @action.bound async resetReach(e: any, item: ITerraceVertexParamsDTO, i: any) {
    try {
      const parsedItem = JSON.parse(JSON.stringify(item));
      const response: any = await TerraceChannelTemplateController.resetChannelTemplateForTerraceVertexParamsDTOUsingPATCH(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId, parsedItem);

      if (response && response.data) {
        MessageCenter.clearMessages();
        if (response.data.errorResponse && response.data.errorResponse.errorType === 'ERROR' && response.data.errorResponse.errorMessage && response.data.errorResponse.errorCode) {
          MessageCenter.addNotification(response.data.errorResponse.errorMessage, response.data.errorResponse.errorType, response.data.errorResponse.errorCode);
        }
        else {
          // Show if response.data has one or more information messages
          if (response.data.errorResponse && response.data.errorResponse.errorType === 'INFORMATION' && response.data.errorResponse.errorMessage && response.data.errorResponse.errorCode) {
            MessageCenter.addNotification(response.data.errorResponse.errorMessage, response.data.errorResponse.errorType, response.data.errorResponse.errorCode);
          }
          // Show if response.data has one or more warning messages
          if (response.data.errorResponse && response.data.errorResponse.errorType === 'WARNING' && response.data.errorResponse.errorMessage && response.data.errorResponse.errorCode) {
            MessageCenter.addNotification(response.data.errorResponse.errorMessage, response.data.errorResponse.errorType, response.data.errorResponse.errorCode);
          }

          const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
          const station = stations[i];
          const templateName = JSON.parse(JSON.stringify(response.data.templateName));
          const templateParams = JSON.parse(JSON.stringify(response.data.templateParams));
          const slopeLimits = JSON.parse(JSON.stringify(response.data.slopeLimits));

          // Check if values have changed
          const templateNameChanged = templateName !== station.templateName;
          const templateParamsChanged = Object.keys(templateParams).some(key => templateParams[key] !== station.templateParams[key]);
          const slopeLimitsChanged = slopeLimits.some((slope, index) =>
            Object.keys(slope).some(key => slope[key] !== station.slopeLimits[index][key])
          );

          if (templateNameChanged || templateParamsChanged || slopeLimitsChanged) {
            const updatedSlopeLimits: ISlopeLimits[] = response.data.slopeLimits.map((slope: any) => ({
              slopeName: slope.slopeName,
              slopeMode: slope.slopeMode,
              slopeValue: slope.slopeValue,
              isLimitEnforced: slope.isLimitEnforced,
              limitingMode: SlopeModeToLimitEnum[slope.slopeMode],
              slopeLimit: slope.slopeLimit
            }));
            // Update templateParams and slopeLimits
            station.templateName = response.data.templateName;
            station.templateParams = { ...response.data.templateParams };
            station.slopeLimits = updatedSlopeLimits;
            station.status = 'IN PROGRESS';

            // Add to PEM
            const resetTerraceVertexData = new Map();
            resetTerraceVertexData.set(station.reachId, station);

            // Synchronize R-0 and R-1
            if (station.reachId === 'R-1') {
              // If resetting R-1, also update R-0
              const r0Index = stations.findIndex(s => s.reachId === 'R-0');
              if (r0Index !== -1) {
                stations[r0Index].templateName = response.data.templateName;
                stations[r0Index].templateParams = { ...response.data.templateParams };
                stations[r0Index].slopeLimits = updatedSlopeLimits;
                stations[r0Index].status = 'IN PROGRESS';
                resetTerraceVertexData.set('R-0', stations[r0Index]);
              }
            }

            await this.props.addTerraceEdits(resetTerraceVertexData, false, true);
          }
        }
      }
    } catch (error) {
      console.error("Error resetting the reach:", error);
    }
  }

  @action.bound async deleteDrainTableView(e: any, item: ITerraceVertexParamsDTO) {
    if (item) {
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      const index = stations.findIndex(s => s.reachId === item.reachId);

      if (index !== -1) {
        const updatedStation = {
          ...stations[index],
          drainStationDto: {
            drainName: null,
            drainFraction: 0,
            drainOffset: 0,
            ugoAndInlet: null,
            location: {
              id: 0,
              pointName: null,
              description: null,
              tinnable: true,
              visible: true,
              wkid: 0,
              x: 0,
              y: 0,
              z: 0
            }
          },
          drainStation: false
        };

        stations[index] = updatedStation;
        terraceDesignStore.terraceDesignObject.terraceDesignStations = [...stations];
        await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        // Add to PEM
        const editMap = new Map();
        editMap.set(item.reachId, updatedStation);
        await this.props.addTerraceEdits(editMap, false, true);
      }
    }
  }

  @action.bound async deleteBlockTableView(e: any, item: ITerraceVertexParamsDTO) {
    if (item) {
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      const index = stations.findIndex(s => s.reachId === item.reachId);

      if (index !== -1) {
        const updatedStation = {
          ...stations[index],
          blockStationDto: {
            sideSlopeMode: 0,
            sideSlope: 0,
            limits: 0,
            topWidth: 0,
            isSplitSubbasin: false,
            blockHeight: null,
            location: {
              id: 0,
              pointName: null,
              description: null,
              tinnable: true,
              visible: true,
              wkid: 0,
              x: 0,
              y: 0,
              z: 0
            }
          },
          blockStation: false
        };

        stations[index] = updatedStation;
        terraceDesignStore.terraceDesignObject.terraceDesignStations = [...stations];
        await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        // Add to PEM
        const editMap = new Map();
        editMap.set(item.reachId, updatedStation);
        await this.props.addTerraceEdits(editMap, false, true);
      }
    }
  }

  @action.bound async handleEditBlock() {
    // Implement the handle edit block action
  }

  @action.bound handleCloseBlock() {
    this.showBlockModal = false;
    this.selectedStation = [];
    terraceDesignStore.terraceInputsChanged = false
    // Reset validation state
    this.blockValidation = {
      formErrors: {
        topWidth: '',
        sideSlope: '',
        limits: ''
      },
      hasError: false
    };
  }

  @action.bound async handleApplyBlock() {
    // Check validation before proceeding
    if (this.blockModalState.sideSlope <= 0 || this.blockModalState.limits <= 0) {
      if (this.blockModalState.sideSlope <= 0) {
        this.blockValidation.formErrors.sideSlope = ' ';
        this.blockValidation.hasError = true;
        MessageCenter.addNotificationFromCode('Common_10', undefined, 'Side Slope Value');
      }
      if (this.blockModalState.limits <= 0) {
        this.blockValidation.formErrors.limits = ' ';
        this.blockValidation.hasError = true;
        MessageCenter.addNotificationFromCode('Common_10', undefined, 'Limits');
      }
      terraceDesignStore.terraceInputsChanged = false
      return;
    }

    // Ensure we have a valid station selected
    if (this.selectedStation.length === 0 || this.blockValidation.hasError) {
      return;
    }
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    const index = stations.findIndex(s => s.reachId === this.selectedStation[0].reachId);

    if (index !== -1) {
      const updatedStation: ITerraceVertexParamsDTO = {
        ...this.selectedStation[0],
        blockStationDto: this.blockModalState,
        blockStation: true,
      };

      stations[index] = updatedStation;
      terraceDesignStore.terraceDesignObject.terraceDesignStations = [...stations];

      // Add to PEM
      const editMap = new Map();
      editMap.set(this.selectedStation[0].reachId, updatedStation);
      await this.props.addTerraceEdits(editMap, false, true);
    }
    await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    this.showBlockModal = false;

    // split terrace if needed
    const data = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    if (this.selectedStation.length > 0) {
      const i = data.findIndex(s => s.reachId === this.selectedStation[0].reachId);
      if (i !== 0 && i !== data.length - 1) {
        if (data[i - 1].drainStation && data[i + 1].drainStation) {
          await this.onClickSave();
          await this.splitTerrace(data[i].reachId);
        }
        if (terraceDesignStore.terraceType === 22 && (data[i - 1].drainStation || data[i + 1].drainStation)) {
          await this.onClickSave();
          await this.splitTerrace(data[i].reachId);
          await MessageCenter.addNotificationFromCode('Terrace_37', undefined);
        }
      }
    }
    this.selectedStation = [];
  }

  @action.bound handleDrainInputBlur(field: 'drainName' | 'drainFraction' | 'drainOffset', value: string | undefined, initialIndex?) {
    const index = initialIndex === undefined ? 0 : initialIndex + 1;
    if (field === 'drainName') {
      if (initialIndex === undefined) {
        this.drainModalState[field] = value ? value : '';
      } else {
        this.existingDrainStations[initialIndex].drainStationDto[field] = value ? value : '';
      }
      this.validateFieldDrainModal(field, value, index);
    } else {
      const numValue = value === undefined ? 0 : Number(value);
      const roundedValue = Number(numValue.toFixed(2));
      if (initialIndex === undefined) {
        this.drainModalState[field] = roundedValue;
      } else {
        this.existingDrainStations[initialIndex].drainStationDto[field] = roundedValue;
      }

      this.validateFieldDrainModal(field, roundedValue, index);
    }
  }

  @action validateFieldDrainModal(fieldName: string, value, index: number) {
    let fieldValid = true;

    if (fieldName === 'drainName') {
      if (value === undefined || !/^[a-zA-Z0-9 ]+$/.test(value)) {
        fieldValid = false;
        MessageCenter.addNotificationFromCode('Common_16', undefined, 'Drain Name');
      } else {
        fieldValid = true;
      }
    } else if (fieldName === 'drainFraction') {
      if (value === undefined || Number(value) <= 0) {
        fieldValid = false;
        MessageCenter.addNotificationFromCode('Common_10', undefined, 'Drain Fraction');
      } else {
        fieldValid = true;
      }
    } else {
      fieldValid = true;
    }

    if (!fieldValid) {
      if (!this.drainValidation.invalidFields.some(field => field.fieldName === fieldName && field.index === index)) {
        this.drainValidation.invalidFields.push({ fieldName, index });
      }
      this.drainValidation.hasError = true;
    } else {
      this.drainValidation.invalidFields = this.drainValidation.invalidFields.filter(
        field => !(field.fieldName === fieldName && field.index === index)
      );
      if (this.drainValidation.invalidFields.length === 0) this.drainValidation.hasError = false;
    }
  }

  @action.bound handleBlockInputBlur(field: 'topWidth' | 'sideSlope' | 'limits', value: number | undefined) {
    const numValue = value === undefined ? 0 : value;
    const roundedValue = Number(numValue.toFixed(2));
    this.blockModalState[field] = roundedValue;

    // Validate on blur
    if (value !== undefined) {
      if (field === 'topWidth') {
        if (roundedValue < 0) {
          this.blockValidation.formErrors.topWidth = ' ';
          this.blockValidation.hasError = true;
          MessageCenter.addNotificationFromCode('Common_10', undefined, 'Top Width');
        } else {
          this.blockValidation.formErrors.topWidth = '';
          this.blockValidation.hasError = false;
        }
      } else if (field === 'sideSlope' || field === 'limits') {
        if (roundedValue <= 0) {
          this.blockValidation.formErrors[field] = ' ';
          this.blockValidation.hasError = true;
          MessageCenter.addNotificationFromCode('Common_10', undefined, field === 'sideSlope' ? 'Side Slope Value' : 'Limits');
        } else {
          this.blockValidation.formErrors[field] = '';
          this.blockValidation.hasError = false;
        }
      }
    }
  }

  @action.bound handleCloseDrain() {
    this.showDrainModal = false;
    this.selectedStation = [];
    terraceDesignStore.terraceInputsChanged = false
    this.drainValidation = {
      hasError: false,
      invalidFields: [] as { fieldName: string; index: number }[]
    };
  }

  @action.bound async handleApplyDrain() {
    if (this.totalDrainFraction > 100) {
      MessageCenter.addErrorFromCode('Terrace_29');
      return;
    }

    if (this.drainModalState.drainFraction <= 0) {
      if (this.drainModalState.drainFraction <= 0) {
        this.drainValidation.hasError = true;
        MessageCenter.addNotificationFromCode('Common_10', undefined, 'Drain Fraction');
      }
      return;
    }

    if (this.selectedStation.length > 0 && !this.blockValidation.hasError) {
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      const index = stations.findIndex(s => s.reachId === this.selectedStation[0].reachId);

      if (index !== -1) {
        const updatedStation: ITerraceVertexParamsDTO = {
          ...this.selectedStation[0],
          drainStationDto: this.drainModalState,
          drainStation: true,
        };

        stations[index] = updatedStation;

        const editMap = new Map();
        editMap.set(this.selectedStation[0].reachId, updatedStation);

        // Handle existing drain editing
        this.existingDrainStations.forEach((station) => {
          if (station.drainStationDto !== stations[station.stationIndex].drainStationDto) {
            const updatedExistingDrainStation: ITerraceVertexParamsDTO = {
              ...stations[station.stationIndex],
              drainStationDto: station.drainStationDto
            };
            stations[station.stationIndex] = updatedExistingDrainStation;
            editMap.set(stations[station.stationIndex].reachId, updatedExistingDrainStation);
          }
        })

        terraceDesignStore.terraceDesignObject.terraceDesignStations = [...stations];

        // Add to PEM
        await this.props.addTerraceEdits(editMap, false, true);
      }
    }
    await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    this.showDrainModal = false;

    // split terrace if needed
    const data = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    if (this.selectedStation.length > 0) {
      const i = data.findIndex(s => s.reachId === this.selectedStation[0].reachId);
      if (i < data.length - 2 && data[i + 1].blockStation && data[i + 2].drainStation) {
        await this.onClickSaveCalculate();
        await this.splitTerrace(data[i + 1].reachId);
      } else if (i > 1 && data[i - 1].blockStation && data[i - 2].drainStation) {
        await this.onClickSaveCalculate();
        await this.splitTerrace(data[i - 1].reachId);
      }
    }
    this.selectedStation = [];
  }

  @action.bound async handleCancelReachTemplate() {
    this.showEditReachTemplateModal = false;
    this.selectedStation = [];
  }

  @action.bound async handleSaveReachTemplate() {
    if (this.selectedStation.length > 0) {
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      const editMap = new Map();

      // Get all checked stations
      const checkedStations = stations.filter(station =>
        terraceDesignStore.checkedRowStations.includes(station.reachId)
      );

      // Find if we're editing either R-0 or R-1, for synchronization
      const isEditingR0 = checkedStations.some(station => station.reachId === 'R-0');
      const isEditingR1 = checkedStations.some(station => station.reachId === 'R-1');

      // If editing R-0 but not R-1, add R-1 to checked stations
      if (isEditingR0 && !isEditingR1) {
        const r1Station = stations.find(station => station.reachId === 'R-1');
        if (r1Station && !terraceDesignStore.checkedRowStations.includes('R-1')) {
          terraceDesignStore.checkedRowStations.push('R-1');
          checkedStations.push(r1Station);
        }
      }

      // If editing R-1 but not R-0, add R-0 to checked stations
      if (isEditingR1 && !isEditingR0) {
        const r0Station = stations.find(station => station.reachId === 'R-0');
        if (r0Station && !terraceDesignStore.checkedRowStations.includes('R-0')) {
          terraceDesignStore.checkedRowStations.push('R-0');
          checkedStations.push(r0Station);
        }
      }

      // Update all checked stations
      checkedStations.forEach(station => {
        const index = stations.findIndex(s => s.reachId === station.reachId);
        if (index !== -1) {
          const updatedSlopeLimits: ISlopeLimits[] = stations[index].slopeLimits.map((slope, idx) => {
            const templateSlope = this.terraceTemplateVM.reachTemplateForm.slopeLimits[idx];
            return {
              ...slope,
              slopeMode: SlopeModeToLimitEnum[templateSlope.slopeMode],
              slopeValue: templateSlope.slopeValue,
              isLimitEnforced: templateSlope.isLimitEnforced,
              limitingMode: templateSlope.slopeMode,
              slopeLimit: typeof templateSlope.slopeLimit === 'string' ?
                parseFloat(templateSlope.slopeLimit) :
                templateSlope.slopeLimit
            };
          });

          const updatedTemplateParams = {
            ...stations[index].templateParams,
            bottomWidth: this.terraceTemplateVM.reachTemplateForm.bottomWidth,
            frontHeight: this.terraceTemplateVM.reachTemplateForm.frontHeight,
            topWidth: this.terraceTemplateVM.reachTemplateForm.topWidth,
            cutSlope: updatedSlopeLimits[0].slopeValue,
            frontSlope: updatedSlopeLimits[1].slopeValue,
            backSlope: updatedSlopeLimits[2].slopeValue,
            cutSlopeMode: SlopeModeChannelEnum[this.terraceTemplateVM.reachTemplateForm.slopeLimits[0].slopeMode],
            frontSlopeMode: SlopeModeChannelEnum[this.terraceTemplateVM.reachTemplateForm.slopeLimits[1].slopeMode],
            backSlopeMode: SlopeModeChannelEnum[this.terraceTemplateVM.reachTemplateForm.slopeLimits[2].slopeMode]
          };

          const updatedStation: ITerraceVertexParamsDTO = {
            ...stations[index],
            status: "IN PROGRESS",
            templateName: "User Defined",
            templateParams: updatedTemplateParams,
            slopeLimits: updatedSlopeLimits,
          };

          stations[index] = updatedStation;
          editMap.set(station.reachId, updatedStation);
        }
      });

      // Clear all checked stations
      terraceDesignStore.checkedRowStations = [];

      terraceDesignStore.terraceDesignObject.terraceDesignStations = [...stations];
      await this.props.addTerraceEdits(editMap, false, true);

      await this.profileStoreRef?.createReachFromSavedDesign(
        terraceDesignStore.terraceDesignObject.terraceDesignStations,
        terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId
      );

      terraceDesignStore.terraceInputsChanged = true;
      // this.uiState = 'free';
      this.showEditReachTemplateModal = false;
      this.selectedStation = [];
    }
  }
  @action.bound setElementActions(tab: string, item: any) {
    const stationCount = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;
    let defaultActions = {};

    const length = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;
    const lastReachId = terraceDesignStore.terraceDesignObject.terraceDesignStations[length - 1].reachId === 'END' ? terraceDesignStore.terraceDesignObject.terraceDesignStations[length - 2].reachId : terraceDesignStore.terraceDesignObject.terraceDesignStations[length - 1].reachId;
    // Only add default station actions for non START/END/R-0 rows
    if (item.reachId !== 'START' && item.reachId !== 'R-0' && item.reachId !== 'END') {
      defaultActions = {
        'Insert Station': { action: this.insertStationTerraceTableView },
        'Delete Station': {
          action: this.deleteStationTableView,
          // Disable if there are <= 3 stations in total
          disabled: stationCount <= 3
        },
      };
    }

    const matchVertexAction = (item.reachId === 'R-0' || item.reachId === lastReachId) ? { 'Match Vertex': { action: this.openMatchVertexModal } } : {};
    const disableBlock = (terraceDesignStore.terraceType === 20);
    const disableDrain = (terraceDesignStore.terraceType === 20 || terraceDesignStore.terraceType === 21);

    switch (tab) {
      case 'combined-view':
      case 'station-view':
        const hasDrain = item.drainStation;
        const hasBlock = item.blockStationDto?.blockHeight;

        if (item.reachId === 'START' || item.reachId === 'R-0' || item.reachId === 'END') {
          return {
            [hasBlock ? 'Edit Block' : 'Add Block']: { action: this.addBlockModal, disabled: disableBlock },
            ...(hasBlock && { 'Delete Block': { action: this.deleteBlockTableView } }),
            ...matchVertexAction,
          };
        } else {
          if (hasBlock) {
            return {
              ...defaultActions,
              'Edit Block': { action: this.addBlockModal, disabled: disableBlock },
              'Delete Block': { action: this.deleteBlockTableView },
              ...matchVertexAction,
            };
          } else if (hasDrain) {
            return {
              ...defaultActions,
              'Edit Drain': { action: this.addDrainModal, disabled: disableDrain },
              'Delete Drain': { action: this.deleteDrainTableView },
              ...matchVertexAction,
            };
          } else {
            if (item.reachId === lastReachId) {
              return {
                ...defaultActions,
                'Add Block': { action: this.addBlockModal, disabled: disableBlock },
                ...matchVertexAction,
              };
            } else {
              return {
                ...defaultActions,
                'Add Block': { action: this.addBlockModal, disabled: disableBlock },
                'Add Drain': { action: this.addDrainModal, disabled: disableDrain },
                ...matchVertexAction,
              };
            }
          }
        }
      case 'template-view':
        // For R-0 in template view, don't show any actions
        if (item.reachId === 'R-0') {
          return {};
        }
        // For all other rows in template view, show both actions
        return { ...this.templateViewActions };
      default:
        return defaultActions;
    }
  }

  @action.bound async initialLoadTerraceDesignTable() {
    await this.tableStoreRef?.addRows(terraceDesignStore.terraceDesignObject.terraceDesignStations);
  }

  @observable profileSettingsForm: IProfileSettingsForm = terraceDesignStore.savedProfileSettingsForm;
  @observable savedProfileSettingsForm: IProfileSettingsForm = terraceDesignStore.savedProfileSettingsForm;
  @observable formValidation = {
    formErrors: {
      fineGrid: '',
      lowBankOffset: '',
      sampleInterval: '',
      verticalInterval: '',
    },
    fineGridValid: true,
    lowBankOffsetValid: true,
    sampleIntervalValid: true,
    verticalIntervalValid: true,
    formValid: true
  }
  @action.bound openSettings() {
    MessageCenter.clearMessages();
    this.profileSettingsForm = JSON.parse(JSON.stringify(this.savedProfileSettingsForm));
    this.profileSettingsForm.profileSettingPopUpActive = true;
  }

  @action.bound toggleFineGrid() {
    this.profileSettingsForm.checked.showFineGrid = !this.profileSettingsForm.checked.showFineGrid;
  }

  @action.bound handleCancelProfileSettings() {
    // reset parameters
    this.profileSettingsForm = JSON.parse(JSON.stringify(this.savedProfileSettingsForm));
    this.profileSettingsForm.profileSettingPopUpActive = false;
  }
  @action.bound async handleSaveProfileSettings() {
    terraceDesignStore.terraceInputsChanged = false
    // this.uiState = 'free';
    if (this.profileSettingsForm.input.sampleInterval !== this.savedProfileSettingsForm.input.sampleInterval
      || this.profileSettingsForm.input.verticalInterval !== this.savedProfileSettingsForm.input.verticalInterval
      || this.profileSettingsForm.checked.showFineGrid !== this.savedProfileSettingsForm.checked.showFineGrid
      || this.profileSettingsForm.input.fineGrid !== this.savedProfileSettingsForm.input.fineGrid) {
      this.profileParametersChanged = true;
    }
    this.profileSettingsForm.profileSettingPopUpActive = false;
    this.savedProfileSettingsForm = JSON.parse(JSON.stringify(this.profileSettingsForm));
    terraceDesignStore.savedProfileSettingsForm = this.savedProfileSettingsForm;

    // update centerline and bank profiles and ProfileRequestDTO API call
    if (this.profileParametersChanged) {
      await this.profileStoreRef?.generateTerraceProfile(Number(this.profileSettingsForm.input.sampleInterval), Number(this.profileSettingsForm.input.verticalInterval), terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    }

    let gridValue: number = 0;
    if (this.profileSettingsForm.checked.showFineGrid) {
      gridValue = Number(this.profileSettingsForm.input.fineGrid);
      this.profileStoreRef!.updateGridValues(gridValue);
    } else {
      this.profileStoreRef!.updateGridValues(0);
    }
  }

  protected async handleMouseUpResizeActions(currentViewExtent) {
    if (this.profileStoreRef?._profileView && currentViewExtent) {
      this.profileStoreRef._profileView.extent = currentViewExtent;
      await this.profileStoreRef.redrawMappingProfiles();
      await this.profileStoreRef.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
    }
  }

  @action.bound handleRowMouseEnter(e: any, i: number) {
    this.trackTableRow(i, true);
  }

  @action.bound handleRowMouseLeave(e: any, i: number) {
    this.trackTableRow(i, false);
  }

  private prepareTrackPoint(station) {
    if (station.endStation && station.channelElev) {
      const stationX = designStore.commonHorizontalStretchFactor
        ? (parseFloat(station.endStation.replace('+', '')) * designStore.commonHorizontalStretchFactor)
        : parseFloat(station.endStation.replace('+', ''));
      const stationY = designStore.commonVerticalStretchFactor
        ? station.channelElev * designStore.commonVerticalStretchFactor
        : station.channelElev;
      const point = new Point({
        x: stationX,
        y: stationY,
        spatialReference: this.profileStoreRef?._profileView?.spatialReference
      });
      this.profileStoreRef?.trackProfilePoint(point, station.reachId);
    }
  }

  protected trackTableRow(i: number, onOff: boolean) {
    const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[i];

    // Add check for undefined or null station
    if (!station) {
      console.error(`Station at index ${i} is undefined.`);
      return;
    }

    if (!terraceDesignStore.checkedRowStations.some(e => e === station.reachId)) {
      if (onOff) {
        this.prepareTrackPoint(station);
      } else {
        this.profileStoreRef?.unTrackProfilePoint(station.reachId);
      }
    }

    if (onOff && !(station.reachId === "START")) {
      if (this.profileStoreRef?.hub.MapStore) {
        (this.profileStoreRef?.hub.MapStore as DynamicTerraceMapStore).trackStation(i);
      }
    } else {
      if (this.profileStoreRef?.hub.MapStore) {
        (this.profileStoreRef?.hub.MapStore as DynamicTerraceMapStore).untrackStation(i);
      }
    }
  }

  @action.bound selectAll() {
    const numCheckedStations = terraceDesignStore.checkedRowStations.length;
    const numStations = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;
    const rZeroSelected = terraceDesignStore.checkedRowStations.includes("R-0");
    const allSelected = numCheckedStations === numStations || (numCheckedStations === numStations - 1 && !rZeroSelected);
    terraceDesignStore.checkedRowStations = [];

    if (allSelected) {
      // Untrack all stations
      this.profileStoreRef?.unTrackUnselectedProfilePoints();
    } else {
      // Add each station to checkedRowStations and track
      terraceDesignStore.terraceDesignObject.terraceDesignStations.forEach((station) => {
        terraceDesignStore.checkedRowStations.push(station.reachId);
        this.prepareTrackPoint(station);
      });
    }
  }

  @action.bound handleCheckboxChange(e: any, i: number) {
    const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[i];

    if (e.target.checked) {
      if (!terraceDesignStore.checkedRowStations.includes(station.reachId)) {
        terraceDesignStore.checkedRowStations.push(station.reachId);
      }
      // Only proceed with Point creation if station has necessary properties
      this.prepareTrackPoint(station);
    } else {
      const index = terraceDesignStore.checkedRowStations.indexOf(station.reachId);
      if (index > -1) {
        terraceDesignStore.checkedRowStations.splice(index, 1);
      }
      this.profileStoreRef?.unTrackProfilePoint(station.reachId);
    }
  }

  @action.bound async generateSurface() {
    try {
      designStore.setLoader("Generating terrace surface...", 'Generate Surface', 150, 100);
      await generateSurface(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId, this.mapView);

      if (this.mapView) {
        try {
          await createAllTerraceScreenshot(this.mapView);
        } catch {
          await MessageCenter.addWarningFromCode('Common_20');
        }
      }
    } catch (e) {
      throw new Error("Failed to generate designed surface.");
    } finally {
      designStore.setLoader('');
    }
  }

  @action.bound async onClickSave() {
    await this.saveTerraceDesign('save');
  }
  @action.bound async onClickSaveCalculate() {
    await this.saveTerraceDesign('saveAndCalculate');
  }

  @action.bound addTerraceToMap(terraceData: any, id: number, title: string): void {
    const layerTitle = (title.length > 0 ? title : `Terrace ${id}`);
    if (this.mapView) {
      const terraceLayer = terraceDesignStore.addTerraceToMap(terraceData, this.mapView, id, layerTitle);

      let fullExtent = getTerraceMapExtent(terraceLayer);
      if (fullExtent) {
        fullExtent.expand(1.1);
        this.mapView.goTo(fullExtent);
      }
    }
  }

  @action.bound async displayAlignment() {
    if (!this.mapView) return;

    const layersMap = new Map();
    if (this.mapView.map) {
      this.mapView.map.layers.forEach((lyr) => layersMap.set(lyr.id, lyr));
    }
    const designElementsDataArray: any = await getListOfDesignElements(terraceDesignStore.alignmentDesignElementJsonId.toString());
    let layerId = '';
    designElementsDataArray.forEach((element: any) => {
      layerId = this.displaySingleAlignment(element, layersMap);
    });
    // want to keep the sketch layer on top
    await sleep(1000); // let it redraw
    if (this.mapView) {
      putStructureRepOnTop(this.mapView, layerId);
      putStructureRepOnTop(this.mapView, 'alignment_sketch');
    }
  }

  @action.bound displaySingleAlignment(element: any, layersMap: any): string {
    let layerId = '';
    const isDisplayed = this.removeOtherAlignments(element.designElementId, layersMap);
    if (isDisplayed) return layerId;
    else {
      if (element.json) {
        const jsonData = JSON.parse(element.json);
        layerId = `alignment${element.designElementId}`;
        DisplayAlignment(
          this.mapView?.map,
          jsonData,
          element.designElementName,
          layerId,
          '',
          appConfig.tempHardCodes.wkid
        );
      }
      return layerId;
    }
  }

  removeOtherAlignments(designElementId: number, layersMap: any): boolean {
    let isDisplayed: boolean = false;
    const alignmentRegex = /^alignment\d{5}$/;

    layersMap.forEach((lyr) => {
      if (lyr.id && lyr.id.startsWith('alignment')) {
        if (lyr.id === `alignment${designElementId}`) {
          isDisplayed = true;
        } else if (alignmentRegex.test(lyr.id)) {
          const alignmentGroupLayer = this.mapView?.map.findLayerById(lyr.id);
          if (alignmentGroupLayer) {
            const removedLayer = this.mapView?.map.remove(alignmentGroupLayer);
            removedLayer?.destroy();
          }
        }
      }
    })
    return isDisplayed;
  }

  @action.bound async saveTerraceDesign(savingType: 'save' | 'saveAndCalculate') {
    this.stopTerraceEditingMode();
    try {
      designStore.isLoading = false;
      designStore.loadingMsg = '';
      designStore.processName = '';
      designStore.loadingProgress = 0;

      if (savingType === 'save') {
        designStore.setLoader('Saving Pending Edits...', "Saving", 0, 100);
        if (designStore.terraceGroupsLength > 0) {
          const isSuccess = await saveTerraceReachEdits();
          if (isSuccess) {
            terraceDesignStore.summaryInfo.channelLength = 1;
            await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
          }
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
          if (this.profileParametersChanged) {
            terraceDesignStore.summaryInfo.channelLength = 1;
            await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
          }
        }
      }
      else if (savingType === 'saveAndCalculate') {
        if (!this.profileStoreRef?.checkSecondaryLayerStatus()) {
          this.profileStoreRef?.waterLevelLayer?.removeAll();
          this.profileStoreRef?.constructedTerraceLayer?.removeAll();
          this.profileStoreRef?.settledTerraceLayer?.removeAll();
        }
        if (designStore.terraceGroupsLength > 0) {
          designStore.setLoader('Saving Pending Edits...', "Save & Calculate", 0, 15);
          const isSuccess = await saveTerraceReachEdits();
          if (isSuccess) {
            terraceDesignStore.summaryInfo.channelLength = 1;
            await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
          }
        }
        try {
          designStore.setLoader('Terrace Simulation And Validation...', "Save & Calculate", 16, 95);
          const simulationResp = await TerraceDesignToolsController.terraceSimulationAndValidationUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
          if (simulationResp && simulationResp.data) {
            const simulationRespData: TerraceSimulationAndValidationRespDTO = simulationResp.data
            MessageCenter.clearMessages();
            if (simulationRespData.errorResponse && simulationRespData.errorResponse.errorType === 'ERROR' && simulationRespData.errorResponse.errorCode && simulationRespData.errorResponse.errorMessage) {
              MessageCenter.addNotification(simulationRespData.errorResponse.errorMessage, simulationRespData.errorResponse.errorType, simulationRespData.errorResponse.errorCode);
              //reload table view
              await this.loadTerraceDesignTableView(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
            }
            else if (!simulationRespData.errorResponse || (simulationRespData.errorResponse && simulationRespData.errorResponse.errorType !== 'ERROR' && simulationRespData.errorResponse.errorCode && simulationRespData.errorResponse.errorMessage)) {
              //show if simulationRespData has one or more information messages
              if ((simulationRespData.errorResponse && simulationRespData.errorResponse.errorType === 'INFORMATION' && simulationRespData.errorResponse.errorCode && simulationRespData.errorResponse.errorMessage)) {
                MessageCenter.addNotification(simulationRespData.errorResponse.errorMessage, simulationRespData.errorResponse.errorType, simulationRespData.errorResponse.errorCode);
              }
              //show if simulationRespData has one or more warning messages
              if ((simulationRespData.errorResponse && simulationRespData.errorResponse.errorType === 'WARNING' && simulationRespData.errorResponse.errorCode && simulationRespData.errorResponse.errorMessage)) {
                MessageCenter.addNotification(simulationRespData.errorResponse.errorMessage, simulationRespData.errorResponse.errorType, simulationRespData.errorResponse.errorCode);
              }
              designStore.setLoader('Calculating Terrace channel shape...', "Save & Calculate", 16, 95);
              //proceed with Terrace calculations
              const calculateResp = await TerraceDesignToolsController.calculateTerraceChannelShapeUsingPOST(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
              if (calculateResp && calculateResp.data) {
                const calculateRespData: TerraceDesignShapeResponseDTO = calculateResp.data
                MessageCenter.clearMessages();
                if (calculateRespData.errorResponse && calculateRespData.errorResponse.errorType === 'ERROR' && calculateRespData.errorResponse.errorCode && calculateRespData.errorResponse.errorMessage) {
                  MessageCenter.addNotification(calculateRespData.errorResponse.errorMessage, calculateRespData.errorResponse.errorType, calculateRespData.errorResponse.errorCode);
                  await this.loadTerraceDesignTableView(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
                }
                else if (!calculateRespData.errorResponse || (calculateRespData.errorResponse && calculateRespData.errorResponse.errorType !== 'ERROR' && calculateRespData.errorResponse.errorCode && calculateRespData.errorResponse.errorMessage)) {
                  //show if calculateRespData has one or more information messages
                  if ((calculateRespData.errorResponse && calculateRespData.errorResponse.errorType === 'INFORMATION' && calculateRespData.errorResponse.errorCode && calculateRespData.errorResponse.errorMessage)) {
                    MessageCenter.addNotification(calculateRespData.errorResponse.errorMessage, calculateRespData.errorResponse.errorType, calculateRespData.errorResponse.errorCode);
                  }
                  //show if calculateRespData has one or more warning messages
                  if ((calculateRespData.errorResponse && calculateRespData.errorResponse.errorType === 'WARNING' && calculateRespData.errorResponse.errorCode && calculateRespData.errorResponse.errorMessage)) {
                    MessageCenter.addNotification(calculateRespData.errorResponse.errorMessage, calculateRespData.errorResponse.errorType, calculateRespData.errorResponse.errorCode);
                  }
                  //proceed with fetch TerraceVertexParamsDTO data
                  const isSuccess = await this.loadTerraceDesignTableView(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);

                  if (isSuccess) {
                    const profileData = {
                      constructedTerrace: calculateRespData.constructedTerrace,
                      settledTerrace: calculateRespData.settledTerrace,
                      waterLevel: calculateRespData.waterLevel
                    };

                    if (this.profileStoreRef) {
                      this.profileStoreRef.toggleSecondaryLayersVisibility(true);
                      await this.profileStoreRef.updateTerraceProfiles(profileData);
                      await this.profileStoreRef?.redrawMappingProfiles();
                      await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
                    }

                    if (calculateRespData.terraceStructureShapeDTO) {
                      this.addTerraceToMap(
                        calculateRespData.terraceStructureShapeDTO,
                        terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
                        terraceDesignStore.terraceDesignObject.alignedFeatureName
                      );
                    }

                    designStore.setTerraceSecLayerVisibility = true;
                    terraceDesignStore.terraceInputsChanged = false
                    terraceDesignStore.hasCalculatedData = true;
                    await this.getTerraceReport();
                    terraceDesignStore.generateTerraceSurface = true;
                    // All previous are success, save design state
                    await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignCalculate.string });
                    designStore.setLoader('Calculation finalizing', 'Save & Calculate', 96, 100);
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                }
              }
            }
          }

        } catch (error) {
          MessageCenter.addError((error as Error).message);
        }
      }
    }
    catch (error) {
      console.error('Error in saveTerraceDesign:', error);
      MessageCenter.addError((error as Error).message);
    }
    finally {
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.setLoader('');
      designStore.loadingProgress = 0;
      this.profileParametersChanged = false;
    }
  }

  @action.bound async loadTerraceDesignTableView(alignedElementDesignParametersId) {
    //proceed with fetch TerraceVertexParamsDTO data
    let isSuccess = false;
    const stations: any = [];
    try {
      const response: any = await TerraceDesignToolsController.getTerraceVertexParamsDtoByAlignedElementIdUsingGET(alignedElementDesignParametersId);
      if (response && response.data) {
        const recalculationData = response.data;
        //MessageCenter.clearMessages();
        if (recalculationData.errorResponse && recalculationData.errorResponse.errorType === 'ERROR' && recalculationData.errorResponse.errorCode && recalculationData.errorResponse.errorMessage) {
          MessageCenter.addNotification(recalculationData.errorResponse.errorMessage, recalculationData.errorResponse.errorType, recalculationData.errorResponse.errorCode);
        }
        else if (!recalculationData.errorResponse || (recalculationData.errorResponse && recalculationData.errorResponse.errorType !== 'ERROR' && recalculationData.errorResponse.errorCode && recalculationData.errorResponse.errorMessage)) {
          isSuccess = true;
          //show if recalculationData has one or more information messages
          if ((recalculationData.errorResponse && recalculationData.errorResponse.errorType === 'INFORMATION' && recalculationData.errorResponse.errorCode && recalculationData.errorResponse.errorMessage)) {
            MessageCenter.addNotification(recalculationData.errorResponse.errorMessage, recalculationData.errorResponse.errorType, recalculationData.errorResponse.errorCode);
          }
          //show if recalculationData has one or more warning messages
          if ((recalculationData.errorResponse && recalculationData.errorResponse.errorType === 'WARNING' && recalculationData.errorResponse.errorCode && recalculationData.errorResponse.errorMessage)) {
            MessageCenter.addNotification(recalculationData.errorResponse.errorMessage, recalculationData.errorResponse.errorType, recalculationData.errorResponse.errorCode);
          }
          //proceed with save terraceDesignStations to store
          terraceDesignStore.terraceDesignObject.terraceDesignStations.forEach((terraceDesignStation, index) => {
            const newStation = recalculationData[index];
            const updatedStation = { ...terraceDesignStation, ...newStation };
            stations.push(updatedStation);
          });
          terraceDesignStore.terraceDesignObject.terraceDesignStations = stations;
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
    return isSuccess;
  }

  // shift Terrace elevation
  @action.bound async setShiftElevation(e: any) {
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.cancelFlag = false;
    this.shiftElevation = 0.0.toFixed(1);
    if (designStore.wdtGroupsLength > 0) {
      MessageCenter.addErrorFromCode('WDT_111');
    } else {
      this.toggleShiftElevationDialog();
    }
  }

  @action.bound toggleShiftElevationDialog() {
    this.shiftElevationDialogIsOpen = !this.shiftElevationDialogIsOpen;
  }

  @action.bound cancelShiftElevationDialog() {
    this.cancelFlag = true;
    this.shiftElevationDialogIsOpen = !this.shiftElevationDialogIsOpen;
    this.shiftElevation = 0.0.toFixed(1);
  }

  @action.bound cancel(): void {
    this.shiftElevation = 0.0.toFixed(1);
  }

  @action.bound async checkShiftElevation(e: any) {
    this.cancelFlag = true;
    if (this.shiftElevationDialogIsOpen) this.toggleShiftElevationDialog();

    if (Number(this.shiftElevation)) {
      if (Number(this.shiftElevation) < -2 || Number(this.shiftElevation) > 2) {
        await shiftElevationCallback('Terrace_8', this.shiftTerraceElevation.bind(this), this.cancel.bind(this));
      } else {
        this.shiftTerraceElevation();
      }
    } else {
      this.shiftElevation = '0.0';
    }
    terraceDesignStore.terraceDesignObject.DisableSaveButtonDesignPage = false;
    terraceDesignStore.terraceDesignObject.disableCalculateButtonDesignPage = false;
    terraceDesignStore.terraceDesignObject.hasTerraceCalculations = false;
    designStore.displayResultBanner = false;
  }

  private async shiftTerraceElevation() {
    try {
      this.profileStoreRef?.raiseStartLengthyProcessEvent(true);
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
      const yScale = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : AppConstants.profileStretchFactor;
      const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
      let isAboveCenterline = false;
      let isExceedDistance = false;
      stations.forEach((station: any, i: number) => {
        if (i < stations.length) {
          station.channelElev = (Number(station.channelElev) + Number(this.shiftElevation)).toFixed(1);
          station.cutFill = parseFloat((parseFloat(station.channelElev) - station.groundElev).toFixed(1));
          const x = parseStationString(station.endStation) * xScale;
          const y = parseFloat(station.channelElev) * yScale;
          if (!isAboveCenterline) {
            if (this.profileStoreRef?.checkAboveCenterline(x, y)) isAboveCenterline = true;
          }
          if (!isExceedDistance) {
            if (this.profileStoreRef?.verifyDistanceWithinLimit(x, y, 2)) isExceedDistance = true;
          }
        }
      });
      //if (isAboveCenterline) MessageCenter.addWarningFromCode('Terrace_8');
      //if (isExceedDistance) MessageCenter.addWarningFromCode('Terrace_8');

      await this.profileStoreRef?.redrawMappingProfiles();
      const movedTerraceVertexData = new Map();
      stations.forEach((station: any, i: number) => {
        movedTerraceVertexData.set(station.reachId, station);
      });
      await this.props.addTerraceEdits(movedTerraceVertexData, false, true);
      await this.profileStoreRef?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
      this.shiftElevation = 0.0.toFixed(1);
    } finally {
      this.profileStoreRef?.raiseStartLengthyProcessEvent(false);
    }
  }

  @action.bound async handleActionValueInputBlur(event: any) {
    if (!this.changedInputValue) {
      return
    }
    this.changedInputValue = false;
    const { value, name } = event.target;

    setTimeout(async () => {
      if (this.cancelFlag) {
        return
      }
      if (name === 'shiftElevation') {
        if (Number(value)) {
          if (Number(value) < -2 || Number(value) > 2) {
            await MessageCenter.addWarningFromCode('Terrace_8');
          }
          this.shiftElevation = value;
        } else {
          this.shiftElevation = '0.0';
        }
      }
    }, 300);
  }

  @action.bound userLeavingWarningOnOk = () => {
    this.userLeavingWarningIsShown = !this.userLeavingWarningIsShown;
  }

  @action.bound openModalFromProfile(type: 'drain' | 'block' | 'match') {
    terraceDesignStore.checkedRowStations = [];
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.startTerraceEditingMode();
    let modalFunction;
    switch (true) {
      case type === 'drain':
        modalFunction = this.addDrainModal;
        break;
      case type === 'block':
        modalFunction = this.addBlockModal;
        break;
      case type === 'match':
        modalFunction = this.openMatchVertexModal;
        break
    }
    this.profileStoreRef?.startProfileEditTool(modalFunction, type);
  }

  @action.bound async getTerraceReport() {
    try {
      const reportValues = await TerraceDesignToolsController.getTerraceSummaryResultsUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
      if (reportValues && reportValues.data) {
        const reportValuesData = reportValues.data;
        terraceDesignStore.terraceReport = reportValuesData;
        if (reportValuesData.errorResponse && reportValuesData.errorResponse.errorType && reportValuesData.errorResponse.errorCode && reportValuesData.errorResponse.errorMessage) {
          MessageCenter.addNotification(reportValuesData.errorResponse.errorMessage, reportValuesData.errorResponse.errorType, reportValuesData.errorResponse.errorCode);
        } else {
          terraceDesignStore.displayResultBanner = true;
        }
      }
    } catch (err) {
      console.error('error: getTerraceSummaryResultsUsingGET()', err)
    }
  }


  @action.bound async getTerraceSummaryReport() {
    try {
      const reportValues = await TerraceDesignToolsController.getTerraceDesignReportUsingGET(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
      this.terraceDesignReportObj = reportValues.data;
    }
    catch (err) {
      console.error('error: getWaterwaySummaryReport()', err)
    }
  }

  @action.bound async toggleReport() {
    this.saveButtonPopup = true;
    await this.getTerraceSummaryReport();
    return;
  }
  @observable terraceResultsComment: string = `(For water elevation ${terraceDesignStore.terraceReport.waterElevation})`;
  @action.bound setTerraceResultsComment = () => {
    if (terraceDesignStore.terraceReport.waterElevation) {
      this.terraceResultsComment = `(For water elevation ${terraceDesignStore.terraceReport.waterElevation.toFixed(2)})`;
    }
  }
  terraceResultsFields = [
    {
      section: 'Terrace Design',
      fields: {
        terraceLength: 'Terrace Length (ft)',
        drainageBasinArea: 'Drainage/Basin (ac)',
        floodDuration: 'Flood Duration (hr)',
        floodRoutingModel: 'Flood Routing Model',
        waterElevation: 'Water Elevation (ft)',
        requiredStorage: 'Required Storage (ac-in)',
      },
      className: 'col-md-5'
    },
    {
      section: 'Modal Output',
      comment: true,
      fields: {
        totalCut: 'Total Cut (cy)',
        totalFill: 'Total Fill (cy)',
        cutFillBalance: 'Cut/Fill Balance (cy)',
        cutFillRatio: 'Cut/Fill Ratio',
        strippingVolume: 'Stripping Volume (cy)',
      },
      className: 'col-md-4 nobr'
    },
    {
      section: '',
      fields: {
        floodArea: 'Flood Area (ac)',
        computedStorage: 'Computed Storage (ac-in)',
      },
      className: 'col-md-3'
    },
  ];
  @action.bound handleVertexSelect(index) {
    this.profileStoreRef?.removeMatchedVertexLayers(this.mapView);
    this.selectedVertexIndex = index;
    const vertex = this.matchedVertices[index];
    this.profileStoreRef?.displayMatchedVertex(vertex);
    const point = new Point({ x: vertex.x, y: vertex.y, spatialReference: projectsLibraryStore.projectSpatialReference });
    const projectedPoint = esriProjection.project(point, this.mapView!.spatialReference) as Point;
    if (projectedPoint) {
      const mapLyr = new GraphicsLayer({ graphics: [new Graphic({ geometry: projectedPoint, symbol: trackerSymbol })], id: 'selectedVertexMap' });
      this.mapView?.map.add(mapLyr);
    }
  }

  @action.bound async applyMatchedVertex() {
    const vertex = this.matchedVertices[this.selectedVertexIndex!];
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    if (
      // R-0 at/past R-1
      (this.targetStationIndex === 0 && vertex.sourceAlignmentDistance >= parseStationString(stations[1].endStation))
      ||
      // Last R at/behind previous R
      (this.targetStationIndex === stations.length - 1 && vertex.sourceAlignmentDistance <= parseStationString(stations[stations.length - 2].endStation))
    ) {
      await MessageCenter.addWarningFromCode('Terrace_21', [
        {
          callback: async () => {
            MessageCenter.clearMessages();
          },
          text: 'Cancel',
        },
        {
          callback: async () => {
            MessageCenter.clearMessages();
            this.truncateTerraceChannel(vertex);
          },
          text: 'OK',
        },
      ]);
    } else {
      const newVertex = new XyzPoint(vertex.sourceAlignmentDistance, vertex.channelElevation, 0);
      await this.tableStoreRef?.modifyRows(newVertex, this.targetStationIndex, this.props.addTerraceEdits);

      this.selectedVertexIndex = undefined;
      this.profileStoreRef?.removeMatchedVertexLayers(this.mapView);
    }
  }

  private async truncateTerraceChannel(vertex) {
    const stations = terraceDesignStore.terraceDesignObject.terraceDesignStations;
    if (this.targetStationIndex === 0) { // Handle R-0
      for (let i = 1; i < stations.length - 1; i++) {
        if (vertex.sourceAlignmentDistance >= parseStationString(stations[i].endStation)) {
          // Delete reaches the new R-0 passes
          const editedChannelVertexData = new Map();
          const deletedReachId = stations[i].reachId;
          const deleteStation = stations[i];
          deleteStation.reachId = 'Delete';
          editedChannelVertexData.set(deletedReachId, deleteStation);

          stations.splice(i, 1);

          // Update remaining stations
          for (let n = i; n < stations.length; n++) {
            const originalReachId = stations[n].reachId;
            stations[n].reachId = 'R-' + (Number(stations[n].reachId.substring(2)) - 1);
            stations[n].status = 'IN PROGRESS';
            editedChannelVertexData.set(originalReachId, stations[n]);
          }

          await this.props.addTerraceEdits(editedChannelVertexData, false, false);
        }
      }
    } else { // Handle R-last
      for (let i = stations.length - 2; i > 0; i--) {
        if (vertex.sourceAlignmentDistance <= parseStationString(stations[i].endStation)) {
          // Delete previous station
          const editedChannelVertexData = new Map();
          const deletedReachId = stations[i].reachId;
          const deleteStation = stations[i];
          deleteStation.reachId = 'Delete';
          editedChannelVertexData.set(deletedReachId, deleteStation);

          stations.splice(i, 1);

          // Update last reach id
          const originalReachId = stations[stations.length - 1].reachId;
          stations[stations.length - 1].reachId = 'R-' + (Number(stations[stations.length - 1].reachId.substring(2)) - 1);
          stations[stations.length - 1].status = 'IN PROGRESS';
          editedChannelVertexData.set(originalReachId, stations[stations.length - 1]);

          await this.props.addTerraceEdits(editedChannelVertexData, false, false);
        }
      }

      // Update last reach index
      this.targetStationIndex = stations.length - 1;
    }

    // modify the original matched vertex endStation
    const newVertex = new XyzPoint(vertex.sourceAlignmentDistance, vertex.channelElevation, 0);
    await this.tableStoreRef?.modifyRows(newVertex, this.targetStationIndex, this.props.addTerraceEdits);
    this.selectedVertexIndex = undefined;
    this.profileStoreRef?.removeMatchedVertexLayers(this.mapView);
  }

  @action.bound async openMatchVertexModal(e: any, station: ITerraceVertexParamsDTO) {
    this.profileStoreRef?.unTrackUnselectedProfilePoints();
    this.matchSearchDistanceState = {
      distance: 20,
      valid: true
    };
    this.matchedVertices = [];
    this.matchTargetStationName = station.endStation;
    this.targetStationIndex = terraceDesignStore.terraceDesignObject.terraceDesignStations.findIndex((station1) => station1 === station);
    if (this.targetStationIndex > -1) {
      await this.callMatchAPI();
    }
  }

  @action.bound handleDistanceInputChange(e) {
    const value = e.target.value;
    this.matchSearchDistanceState = value === '' ? {
      distance: undefined,
      valid: false
    } : {
      distance: Number(value),
      valid: Number(value) > 0 ? true : false
    }
  }

  @action.bound async handleDistanceInputBlur(e) {
    const value = e.target.value === '' ? undefined : Number(e.target.value);

    if (value !== undefined) {
      if (Number(value) > 0 && Number(value) < 51) {
        this.matchSearchDistanceState.valid = true;
      } else {
        this.matchSearchDistanceState.valid = false;
        this.selectedVertexIndex = undefined;
        this.matchedVertices = [];
        await MessageCenter.addErrorFromCode('Terrace_22');
      }
    }
  }

  @action.bound async callMatchAPI() {
    this.showMatchVertexModal = false;
    this.selectedVertexIndex = undefined;
    this.matchedVertices = [];
    try {
      designStore.setLoader('Loading matches...');
      MessageCenter.clearMessages();
      const coords = (await AlignmentController.getXYByAlignmentAndStationNameUsingPOST({ alignmentDesignElementId: terraceDesignStore.alignmentDesignElementJsonId, stationName: this.matchTargetStationName })).data;
      const params = {
        sessionUuid: designStore.projectsStore.currentSessionId,
        alignedElementParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
        sourceStationPoint: {
          x: coords.x,
          y: coords.y
        },
        searchDistance: this.matchSearchDistanceState.distance
      }

      const result = await TerraceProfileController.getAdjacentTerracesOrWaterwaysUsingPOST(params);

      const resultArray: IAdjacentTerracesOrWaterwaysResponseDTO[] = [];
      if (result?.data && result.data.matchedVertices?.length) {
        result.data.matchedVertices?.forEach((item) => {
          const vertex = {
            ...item,
            selected: false
          } as IAdjacentTerracesOrWaterwaysResponseDTO
          resultArray.push(vertex);
        });
        resultArray.sort((a, b) => a.distance - b.distance);
        this.matchedVertices = resultArray;
        this.handleVertexSelect(0);
      }
    } catch (e) {

    } finally {
      this.showMatchVertexModal = true;
      if (this.matchedVertices.length === 0) {
        MessageCenter.addWarningFromCode('Terrace_17');
      }
      designStore.setLoader('');
    }
  }

  @action.bound closeMatchVertexModal() {
    this.selectedVertexIndex = undefined;
    this.matchedVertices = [];
    this.showMatchVertexModal = false;
    this.profileStoreRef?.removeMatchedVertexLayers(this.mapView);
  }

  @action.bound setValue = (col, rowData) => {
    const isEditable = col.editable;

    let value;
    if (col.name.includes('.')) {
      const [parentKey, childKey] = col.name.split('.');
      value = rowData[parentKey]?.[childKey] ?? '';
    } else {
      value = rowData[col.name];
    }

    if (col.name === 'draiBblock') {
      if (rowData.blockStationDto?.blockHeight) {
        value = rowData.blockStationDto.blockHeight.replace(/_/g, ' ').toLowerCase();
      } else if (rowData.drainStationDto?.drainName != null) {
        value = `${rowData.drainStationDto.drainFraction}%`;
      } else {
        value = '';
      }
    }

    if (col.name === 'grade') {
      value = (value * 100).toFixed(2);
    }

    // Decimal format the value based on the column name
    if (['groundElev', 'cutFill', 'stationDist', 'designHeight'].includes(col.name)) {
      value = value !== null && value !== undefined && value !== ''
        ? parseFloat(value).toFixed(1).replace(/\.0$/, '')
        : value;
    } else if (typeof value === 'number' && !isEditable) {
      value = formatRoundSTrim(value, 2);
    } else if (value === null || value === undefined) {
      value = '';
    }

    return [isEditable, value];
  }

  @action.bound init = async () => {
    // Get design stations
    await terrraceVertexParamsOnDesign();
    // Get calculation results
    // Assumes results are invalid if a reach was changed and saved
    if (terraceDesignStore.uiState === 'ChannelDesignCalculate' || terraceDesignStore.uiState === 'ChannelDesignSurface') {
      await this.calculateAndDisplayTerraceShape();

      await this.getTerraceReport();
      if (terraceDesignStore.uiState === 'ChannelDesignCalculate') {
        terraceDesignStore.generateTerraceSurface = true;
      }
    }
  };

  @action.bound async calculateAndDisplayTerraceShape() {
    try {
      // Copied from save & calculate function above
      const calculateResp = await TerraceDesignToolsController.calculateTerraceChannelShapeUsingPOST(terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
      if (calculateResp && calculateResp.data) {
        const calculateRespData: TerraceDesignShapeResponseDTO = calculateResp.data
        if (calculateRespData.terraceStructureShapeDTO) {
          this.addTerraceToMap(
            calculateRespData.terraceStructureShapeDTO,
            terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
            terraceDesignStore.terraceDesignObject.alignedFeatureName
          );
          designStore.setTerraceSecLayerVisibility = true;
        }
      }
    }
    catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound splitTerrace = async (reachId) => {
    const payload = {
      reachIdAsSplittingLocation: reachId,
      terraceAlignedElementId: +terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId
    }
    try {
      const response = await TerraceDesignToolsController.splitTerraceUsingPOST(payload);
      if (response.data.errorResponse && response.data.errorResponse.errorType && response.data.errorResponse.errorCode && response.data.errorResponse.errorMessage) {
        MessageCenter.addNotification(response.data.errorResponse.errorMessage, response.data.errorResponse.errorType, response.data.errorResponse.errorCode);
      } else if (response.data) {
        terraceDesignStore.newTerraces = [response.data.leadingTerraceAlignedElementId, response.data.trailingTerraceAlignedElementId]
        this.closeAction();
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

}